import { ViewType, ViewObject } from '../base';

import { Subject } from 'rxjs';

import { AppConstants } from '@unpispas/upp-defs';
import { PriceType, PriceInfo } from '@unpispas/upp-defs';
import { SerialType, InvoiceType } from './place';
import { dataService, dataTransaction } from '../../data';

import { languageService } from '@unpispas/upp-base';
import { stateService } from '@unpispas/upp-base';
import { wsconnectService } from '../../wsconnect';
import { wsloggerService } from '../../wsconnect';
import { toastService } from '@unpispas/upp-base';

import { TicketPayment } from '../objects/ticket';
import { TicketSource } from '../objects/ticket';
import { TicketChangeReason } from '../objects/ticketchange';

import { AskWaiter } from '../objects/askwaiter';
import { Ticket } from '../objects/ticket';
import { Session } from '../objects/session';
import { Till } from '../objects/till';
import { Client } from '../objects/client';
import { TicketProduct } from '../objects/ticketproduct';
import { TicketOption } from '../objects/ticketoption';
import { TicketOffer } from '../objects/ticketoffer';
import { TicketExtra } from '../objects/ticketextra';
import { TicketDiscount } from '../objects/ticketdiscount';
import { TicketInvoice } from '../objects/ticketinvoice';
import { TicketChange } from '../objects/ticketchange';
import { Ticketbai } from '../objects/ticketbai';
import { Verifactu } from '../objects/verifactu';
import { MixedPayment } from '../objects/mixedpay';
import { PinpadPay } from '../objects/pinpadpay';
import { TicketData } from '../objects/ticketdata';

import { PlaceView } from './place';
import { QrCodeView } from './qrcode';
import { ProductView } from './product';
import { ProductOptView } from './productopt';
import { OfferView } from './offer';
import { ExtraView } from './extra';
import { TicketProductView } from './ticketproduct';
import { TicketOfferView } from './ticketoffer';
import { TicketExtraView } from './ticketextra';
import { TicketDiscountView } from './ticketdiscount';
import { DiscountView } from './discount';
import { PayAccountView } from './payaccount';

import { LogActions } from '../../events';

import { AuditInfoLineEntry } from '../objects/audit';
export type TicketLinesCount = {
    addlines: AuditInfoLineEntry;
    dellines: AuditInfoLineEntry;
};

export interface ToCartInfo {
    market?: number | null;
    weight?: number | null;
    comments?: string | null;
    fixed?: number | null;
    type?: 'BAR' | 'KIT' | null;
}

export interface TicketUnits {
    product: TicketProductView;
    units: number;
}

export interface MergeOptions {
    allowDiscountLoss?: boolean;
}

/****************************/
/* ORDER LOGIC              */
/****************************/

/**
 * Lifecycle event for an order slip. Not persisted on the ticket: resolved when
 * the order is requested (`ToInsert` / `!PrintedFlag` → `new`, later pending → `set`,
 * cancel with a prior successful order print → `del`). Manual reprint of the
 * full preparation slip uses `'cpy'` (does not consume pending delta).
 */
export type OrderAction = 'new' | 'set' | 'del' | 'cpy';

/** Payload emitted on `OnOrder` after a commit (or immediate `DoOrder` / `DoReorder`). */
export interface OrderInfo {
    action: OrderAction;
}

/**
 * Encapsulates kitchen/bar order-slip intent, parallel to `TicketPrint` but
 * independent of fiscal receipt printing.
 *
 * After commit, when `ToOrder`, `OrderTicket()` emits `OnOrder` with the
 * resolved action. Emit is fire-and-forget: consumers must not block or roll
 * back the commit. Destination resolution, physical dispatch, `SetNotified()` /
 * `PrintedFlag` on success and the follow-up persist live in `queueService`
 * (`feature/ticket`); `UppTicketOrderComponent` only renders each slip's DOM.
 */
class TicketOrder {
    private _onOrder = new Subject<OrderInfo>();
    public OnOrder = this._onOrder.asObservable();

    constructor(private ticket: TicketView, private data: dataService){
        // nothing to do
    }

    /** Rebinds after copy overwrite so the same Subject keeps queue subscribers. */
    Bind(ticket: TicketView): void {
        this.ticket = ticket;
    }

    /**
     * Lines with `GetNotified() !== 0` (includes cancelled `DE` with a
     * pending removal). Not a print document source — destination filtering
     * and separators live in the order print mapper.
     */
    get ProductsToOrder(): Array<TicketProductView> {
        return this.ticket.PrintOrder.filter(
            (_view) => _view.GetNotified() !== 0
        );
    }

    /**
     * True when commit should emit an order slip: cancelled tickets only if
     * `PrintedFlag` (prior successful order → `del`); otherwise when there is
     * pending line delta. Parallel to `TicketPrint.ToPrint`.
     */
    get ToOrder(): boolean {
        if (this.ticket.IsCancelled){
            return this.ticket.PrintedFlag;
        }

        return this.ProductsToOrder.length > 0;
    }

    /**
     * Flushes order intent after commit: emits `OnOrder` with the resolved
     * `OrderAction`. Caller should gate with `ToOrder` (commit path does).
     * Does not write `PrintedFlag`.
     */
    OrderTicket(): void {
        if (!this.ToOrder){
            return;
        }

        if (this.ticket.IsCancelled){
            console.log("[TICKET ORDER] Order job sent (del)");
            try {
                this._onOrder.next({ action: 'del' });
            }
            catch (err) {
                console.error("[TICKET ORDER] OnOrder consumer failed", err);
            }
            return;
        }

        const _action: OrderAction = this.ticket.PrintedFlag ? 'set' : 'new';
        console.log("[TICKET ORDER] Order job sent (" + _action + ")");
        try {
            this._onOrder.next({ action: _action });
        }
        catch (err) {
            // Best-effort: order consumers must not roll back a committed ticket
            console.error("[TICKET ORDER] OnOrder consumer failed", err);
        }
    }

    /**
     * Manual full-slip reprint: emits `OnOrder` with `'cpy'` for every current
     * preparation line, without requiring pending delta and without cancel path.
     * Dispatch must not `SetNotified` / mutate `PrintedFlag`.
     */
    CopyOrder(): void {
        if (this.ticket.IsCancelled){
            return;
        }

        if (this.ticket.ProductsToPrepare.length === 0){
            return;
        }

        console.log("[TICKET ORDER] Order job sent (cpy)");
        try {
            this._onOrder.next({ action: 'cpy' });
        }
        catch (err) {
            console.error("[TICKET ORDER] OnOrder consumer failed", err);
        }
    }
}

/****************************/
/* PRINT LOGIC              */
/****************************/

export interface PrintInfo {
    printer?: any,      // when the device is explicetlly chosen
    isauto?: boolean,   // performed due to a payment operation
    drawer?: boolean    // drawer opening has been requested
};

/**
 * Encapsulates ticket print intent and dispatching.
 *
 * It supports two modes:
 * - immediate dispatch (`DoPrint(..., oncommit=false)`)
 * - deferred dispatch after commit (`DoPrint(..., oncommit=true)` + `PrintTicket()`)
 */
class TicketPrint {
    /** Pending print request flag used by commit flow. */
    private _print_requested = false;
    /** Pending drawer-open request to include in the print payload. */
    private _dopen_requested = false;
    /** Pending "automatic/on-payment" print marker. */
    private _onpay_requested = false;

    private _onPrint = new Subject<PrintInfo>();
    private _onDrawer = new Subject<PrintInfo>();

    public OnPrint = this._onPrint.asObservable();
    public OnDrawer = this._onDrawer.asObservable();

    constructor(private ticket: TicketView){
        // nothing to do
    }

    /** Rebinds after copy overwrite so the same Subject keeps queue subscribers. */
    Bind(ticket: TicketView): void {
        this.ticket = ticket;
    }

    /** True when the next deferred print should request drawer opening. */
    get ToOpen(): boolean {
        return this._dopen_requested;
    }

    /** True when a deferred print has been requested and not flushed yet. */
    get ToPrint(): boolean {
        return this._print_requested;
    }

    /** True when deferred print was requested from a payment operation. */
    get IsAuto(): boolean {
        return this._onpay_requested;
    }

    /**
     * drawer: The drawer must be opened when printing
     * isauto: The printing operation is configured on ticket payment
     * oncommit: Do not send the printing operation until the commit is completed
     *
     * When `oncommit` is true, this method only accumulates flags.
     * When `oncommit` is false, it emits the print event immediately.
     */
    DoPrint(drawer = false, isauto = false, oncommit = false){
        if (oncommit){
            this._print_requested ||= true;
            this._dopen_requested ||= drawer;    
            this._onpay_requested ||= isauto;
        }
        else {
            const _info: PrintInfo = {
                isauto: isauto,
                drawer: drawer
            };

            this._onPrint.next(_info);
        }
    }

    /**
     * Emits a drawer-open event and clears pending drawer request state.
     */
    OpenDrawer(){
        const _info: PrintInfo = {
            isauto: this._onpay_requested,
            drawer: true
        };

        this._dopen_requested = false;
        this._onDrawer.next(_info);
    }

    /**
     * Flushes deferred print flags by emitting one print event and then
     * resetting all pending print state.
     */
    PrintTicket(){
        const _info: PrintInfo = {
            isauto: this._onpay_requested,
            drawer: this._dopen_requested
        };

        this._onPrint.next(_info);

        // ticket is printed: reset
        this._print_requested = false;
        this._dopen_requested = false;
        this._onpay_requested = false;
    }
}

/****************************/
/* COMMIT LOGIC             */
/****************************/

export class TicketCommit {
    /** Serializes LOGIN fiscal numbering + TBAI/VFTU sign + chain cache updates per process. */
    private static _fiscalChainTail: Promise<void> = Promise.resolve();

    private static async _WithFiscalChainLock<T>(fn: () => Promise<T>): Promise<T> {
        const _prev = TicketCommit._fiscalChainTail;
        let _release!: () => void;
        TicketCommit._fiscalChainTail = new Promise<void>((resolve) => {
            _release = resolve;
        });
        await _prev;
        try {
            return await fn();
        }
        finally {
            _release();
        }
    }


    private _onCommit = new Subject<void>();
    public OnCommit = this._onCommit.asObservable();

    constructor(private data: dataService, private ticket: TicketView){
        // nothing to do
    }

    private _connect: wsconnectService | null = null;
    private get wslogger(): wsloggerService {
        if (!this._connect){
            this._connect = this.data.injector.get(wsconnectService);
        }
        return this._connect.logger;
    }

    private _state: stateService | null = null;
    private get state(): stateService {
        if (!this._state){
            this._state = this.data.injector.get(stateService);
        }
        return this._state;
    }

    private get IsGuest(): boolean {
        return this.state.Access === 'GUEST';
    }

    private get place(): PlaceView | null {
        return this.ticket.place;
    }

    private _ticketprint: TicketPrint | null = null;
    private get print(): TicketPrint {
        if (this._ticketprint === null){
            this._ticketprint = new TicketPrint(this.ticket);
        }
        return this._ticketprint;
    }

    private _ticketorder: TicketOrder | null = null;
    private get order(): TicketOrder {
        if (this._ticketorder === null){
            this._ticketorder = new TicketOrder(this.ticket, this.data);
        }
        return this._ticketorder;
    }

    async CommitTicket(payment: TicketPayment | null, transaction: dataTransaction | null): Promise <void> {
        const _ini = performance.now();

        // normalize to canonical ticket before fiscal checks and numbering
        const _copyof = this.ticket.CopyOf as Ticket;
        if (_copyof){
            // Unpaid copy must not overwrite a paid original (would clear the payment).
            if (!this.ticket.PaidFlag && _copyof.PaidFlag){
                throw new Error('[TICKET] Cannot overwrite: original already paid elsewhere');
            }

            this.ticket = this.data.alives.get(this.ticket.object.Overwrite()) as TicketView;

            this._ticketprint?.Bind(this.ticket);
            this._ticketorder?.Bind(this.ticket);
        }

        // update ticket status (if not avaiable)
        if (this.ticket.status == 'PR'){
            this.ticket.status = 'AC';
        }

        if (this.ticket.IsEmpty){
            this.ticket.status = 'CC';
        }

        const _orderpromise = this._OrderNo();          // request for a orderno (async)
        this._OpenDrawer(_ini);                         // open the drawer before commiting    
        await _orderpromise;                            // wait to receive the order number
        await this._TicketChange(_ini);                 // create the ticketchange instance
        // Every persisted ticket must carry a commercial registry snapshot.
        // `_TicketChange` already refreshes in `finally`; this is the hard gate.
        this._EnsureTicketData(_ini);
        this._PrintTicket(_ini);                        // send the ticket to the printer
        await this._CommitTicket(transaction, _ini);    // commit the ticket (throws on failure)
        this._OrderTicket(_ini);                        // kitchen/bar order slip (best-effort, after commit)
        this._NotifyTicket(_ini);                       // notify local activity
    }

    /**
     * Requests a receipt print. When `oncommit` is false (manual TICKET /
     * options print), refreshes the draft registry first so the receipt
     * matches the live cart; paid fiscal snapshots are left untouched.
     */
    PrintTicket(drawer = false, isauto = false, oncommit = false){
        if (!oncommit){
            const _registry = this.ticket.ticketdata?.Registry;
            if (_registry == null || _registry.fiscal?.status === 'DRAFT'){
                this._RefreshData(performance.now());
            }
        }
        
        return this.print.DoPrint(drawer, isauto, oncommit);
    }

    get OnPrint(){
        return this.print.OnPrint;
    }

    /**
     * Immediately emit an order slip if there is pending delta (manual/retry path).
     * Normal commit flow calls `order.OrderTicket()` after `CommitTicket` instead.
     */
    OrderTicket(){
        return this.order.OrderTicket();
    }

    /**
     * Manual reprint of the full kitchen/bar slip as `'cpy'` (no pending-delta gate).
     */
    CopyOrder(){
        return this.order.CopyOrder();
    }

    get OnOrder(){
        return this.order.OnOrder;
    }

    /**
     * Lines still pending on an order slip (`GetNotified() !== 0`).
     * Dispatch uses this after a successful device print to know what to
     * `SetNotified()` (intersected with lines covered by that destination).
     */
    get ProductsToOrder(): Array<TicketProductView> {
        return this.order.ProductsToOrder;
    }

    /**********************************/
    /* PRIVATE METHODS                */
    /**********************************/

    private _OrderNo(): Promise<void> {
        return new Promise <void> ((resolve) => {
            if (this.ticket.orderno){
                resolve();
            }
            else {
                // TODO: request a unique orderno from the server.
                // similar to v3, try to get and reserve a free orderno before the commit
                resolve();
            }
        });
    }

    private _OpenDrawer(_ini: DOMHighResTimeStamp): void {
        if (this.print.ToOpen){
            this.print.OpenDrawer();
        }

        console.info("[TICKET] On drawer check completed in " + (performance.now() - _ini).toFixed(2) + " ms")
    }    

    private async _TicketChange(_ini: DOMHighResTimeStamp): Promise <void> {
        try {
            const lastchange: TicketChange | null = this.ticket.lastchange;

            let changereason: TicketChangeReason | null = null;
            if (lastchange){
                changereason = lastchange.ChangeReason;
            }
            else if (this.ticket.IsCancelled){
                changereason = 'C';
            }
            else {
                changereason = 'F';
            }

            // reopened ticket being repaid: always rectificative
            if (lastchange !== null && this.ticket.payment != null && this.ticket.ReopenedFlag && !changereason){
                changereason = 'P';
            }

            if (!changereason){
                return;
            }

            if (this.IsGuest && !(['F', 'I', 'D', 'C'].includes(changereason))) {
                throw new Error("[TICKET] GUEST cannot emit TicketChange reason '" + changereason + "'");
            }

            // Cancel without MarkPaid: no TicketChange (GUEST and LOGIN). Covers unpaid
            // carts and pending till requests (payment set, still !PaidFlag).
            if (changereason === 'C' && !this.ticket.object.PaidFlag){
                if (lastchange === null && this.ticket.payment == null){
                    if (this.IsGuest){
                        console.info("[TICKET] GUEST send without invoice number (server will assign)");

                        this.ticket.series = null;
                        this.ticket.invceno = null;
                    }
                    else if (!this.ticket.invceno?.startsWith('X')){
                        const [_serial, _invice] = await this._NextInvoice(SerialType.X, InvoiceType.X);

                        this.ticket.series = _serial;
                        this.ticket.invceno = _invice;

                        console.info("[TICKET] " + this.ticket.series + " " + this.ticket.invceno);
                    }
                }
                else if (this.IsGuest){
                    this.ticket.series = null;
                    this.ticket.invceno = null;
                }

                return;
            }

            if (this.IsGuest && changereason === 'C'){
                throw new Error("[TICKET] GUEST cannot emit TicketChange reason 'C'");
            }
    
            if (lastchange === null) {
                // first commit: provisional X numbering for unpaid tickets, no TicketChange
                if (this.ticket.payment == null){
                    if (this.IsGuest){
                        console.info("[TICKET] GUEST send without invoice number (server will assign)");

                        this.ticket.series = null;
                        this.ticket.invceno = null;

                        return;
                    }

                    if (!this.ticket.invceno?.startsWith('X')){
                        const [_serial, _invice] = await this._NextInvoice(SerialType.X, InvoiceType.X);

                        this.ticket.series = _serial;
                        this.ticket.invceno = _invice;

                        console.info("[TICKET] " + this.ticket.series + " " + this.ticket.invceno);
                    }

                    return;
                }
            }
            else {  // reopened ticket without payment has no fiscal impact
                if (this.ticket.payment == null){
                    return;
                }
            }
    
            // create TicketChange: first (F, prev=null) or rectificative (P, prev=lastchange)
            console.info("[TICKET] Creating ticket change in " + (performance.now() - _ini).toFixed(2) + " ms..")

            if (this.IsGuest){    // GUEST: server owns F/I/D numbering — no client NextDeviceTicket.
                this.ticket.series = null;
                this.ticket.invceno = null;

                console.info("[TICKET] Sending ticket without invoice number (will be calculated on server)");

                const nextchange = new TicketChange(null, this.data);
                if (nextchange){
                    this._FillTicketChange(nextchange, lastchange, changereason);
                }
                this.ticket.AddChange(nextchange);
                this._RefreshData(_ini);
            }
            else {
                // LOGIN: serialize number + sign + StoreCachedChain (chronological SIF chain).
                await TicketCommit._WithFiscalChainLock(async () => {
                    await this._InvoiceNumber(changereason);

                    const nextchange = new TicketChange(null, this.data);
                    if (nextchange){
                        this._FillTicketChange(nextchange, lastchange, changereason);
                    }
                    this.ticket.AddChange(nextchange);

                    console.info("[TICKET] Creating taxes information in " + (performance.now() - _ini).toFixed(2) + " ms..")
                    this._RefreshData(_ini);

                    await this.SendVerifactu(nextchange);
                    await this.SendTicketbai(nextchange);

                    console.info("[TICKET] Completed taxes information in " + (performance.now() - _ini).toFixed(2) + " ms..")
                });
            }
    
        }
        finally {
            this._RefreshData(_ini);                        // refresh ticket registry snapshot (with fiscal data)
        }
    }

    private _FillTicketChange(
        nextchange: TicketChange,
        lastchange: TicketChange | null,
        changereason: TicketChangeReason
    ): void {
        const _priceinfo = this.ticket.Reset();

        nextchange.created = new Date();
        nextchange.updated = new Date();
        nextchange.ticket = this.ticket.object;
        nextchange.prev = lastchange;
        nextchange.session = this.data.session;
        nextchange.client = null;
        nextchange.reason = changereason;
        nextchange.series = this.ticket.series;
        nextchange.invoice = this.ticket.invceno;
        nextchange.total = _priceinfo.tax.total;
        nextchange.base = _priceinfo.tax.base;
        nextchange.taxes = _priceinfo.tax.taxes;

        if (this.ticket.invoice){
            nextchange.client = this.ticket.invoice.client;
        }
    }

    /** Refreshes commercial/fiscal registry snapshot on TICKETDATA. */
    _RefreshData(_ini: DOMHighResTimeStamp): void {
        this.ticket.Reset();

        let _ticketdata: TicketData | null = this.ticket.ticketdata;
        if (_ticketdata == null){
            _ticketdata = new TicketData(null, this.data);
            _ticketdata.ticket = this.ticket.object;
            this.ticket.AddData(_ticketdata);
        }

        _ticketdata.Refresh(this.ticket);
        console.info("[TICKET] Ticket registry data refreshed in " + (performance.now() - _ini).toFixed(2) + " ms");
    }

    /**
     * Guarantees a live `TICKETDATA` registry before the ticket is persisted.
     * `CommitTicket` is the single path that may create a server ticket; payment
     * flows (till request / PAYLINE) only update payment fields on that row.
     */
    private _EnsureTicketData(_ini: DOMHighResTimeStamp): void {
        if (this.ticket.ticketdata == null){
            this._RefreshData(_ini);
        }

        if (this.ticket.ticketdata == null){
            throw new Error('[TICKET] CommitTicket requires TICKETDATA registry');
        }
    }

    private _PrintTicket(_ini: DOMHighResTimeStamp): void {
        if (this.print.ToPrint){
            this.print.PrintTicket();
        }

        console.info("[TICKET] Ticket print check completed in " + (performance.now() - _ini).toFixed(2) + " ms")
    }

    /**
     * Emits kitchen/bar order intent after the ticket is already committed.
     * Failures in `OnOrder` subscribers must not roll back the commit.
     */
    private _OrderTicket(_ini: DOMHighResTimeStamp): void {
        if (this.order.ToOrder){
            this.order.OrderTicket();
        }

        console.info("[TICKET] Ticket order check completed in " + (performance.now() - _ini).toFixed(2) + " ms")
    }

    private _NotifyTicket(_ini: DOMHighResTimeStamp): void {
        if (this.place) {
            this.wslogger.LogTicketCommit(this.place.object, this.ticket.object);
        }

        console.info("[TICKET] Local activity notify completed in " + (performance.now() - _ini).toFixed(2) + " ms")
    }

    /** Persists ticket rows (and invoice client when needed) without TicketChange. */
    async _CommitTicket(transaction: dataTransaction | null, _ini: DOMHighResTimeStamp): Promise <void> {
        const _ticket: Ticket = this.ticket.object;
        const _client: Client | null = _ticket.invoice?.client ?? null;

        let _transaction = transaction;
        const _owntransaction = !_transaction && !!_client;
        if (_owntransaction){
            _transaction = this.data.transaction;
        }

        // Before flush: guest sent empty numbering and expects the server to fill it.
        const _validwoutinvoice = this.IsGuest && (!this.ticket.series || !this.ticket.invceno);

        if (_transaction){
            if (_client){
                await _transaction.push(_client);
            }

            if (!(await _transaction.push(_ticket))){
                throw new Error('[TICKET] Commit failed for ticket [' + String(_ticket.objid ?? _ticket._uuid) + ']');
            }

            console.info("[TICKET] Commited ticket [" + _ticket.objid + "] with flags [" + _ticket.flags + "] completed in " + (performance.now() - _ini).toFixed(2) + " ms");
            this._onCommit.next();

            if (_owntransaction){
                await _transaction.flush(this.IsGuest);
            }
            else if (this.IsGuest){
                await this.data.Flush(true);
            }
        }
        else {
            // Await so OnOrder / SetNotified cannot race an in-flight commit
            if (!(await _ticket.DoCommit(this.IsGuest))){
                throw new Error('[TICKET] Commit failed for ticket [' + String(_ticket.objid ?? _ticket._uuid) + ']');
            }

            console.info("[TICKET] Commited ticket [" + _ticket.objid + "] with flags [" + _ticket.flags + "] completed in " + (performance.now() - _ini).toFixed(2) + " ms");
            this._onCommit.next();
        }

        // After flush: server assigned numbers → sync TICKETDATA (not if already numbered).
        if (_validwoutinvoice && this.ticket.series && this.ticket.invceno){
            this._RefreshData(_ini);

            const _ticketdata = this.ticket.ticketdata;
            if (_ticketdata?.ToUpdate){
                await _ticketdata.DoCommit(true);
            }
        }

        // adds the ticket into the place
        const place = this.ticket.place;
        if (place){
            place.AddTicket(_ticket);
        }

        // adds the ticket into the table
        const qrcode = this.ticket.qrcode;
        if (qrcode){
            qrcode.AddTicket(_ticket);
        }
    }

    /**********************************/
    /* TICKET CHANGE METHODS          */
    /**********************************/    

    private async _NextInvoice(serialtype: SerialType, invoicetype: InvoiceType): Promise <(string | null)[]> {
        if (!this.ticket.place || this.IsGuest){
            return [ null, null ];
        }

        return this.ticket.place.NextDeviceTicket(serialtype, invoicetype);
    }
    
    private async _InvoiceNumber(reason: TicketChangeReason){
        let _serial: string | null = null;
        let _invice: string | null = null;

        switch(reason){
            case 'F':
                if (this.ticket.invoice == null){
                    [_serial, _invice] = await this._NextInvoice(SerialType.S, InvoiceType.T);
                }
                else {
                    [_serial, _invice] = await this._NextInvoice(SerialType.C, InvoiceType.C);
                }
                break;

            case 'P':
            case 'D':
                [_serial, _invice] = await this._NextInvoice(SerialType.R, InvoiceType.R);
                break;

            case 'I':
                [_serial, _invice] = await this._NextInvoice(SerialType.F, InvoiceType.F);
                break;

            case 'C':
                if ((this.ticket.series == null) || (this.ticket.invceno == null)){
                    throw new Error("[TICKET] invoice number not provided when it should");
                }

                _serial = this.ticket.series;
                _invice = this.ticket.invceno;
                
                break;
        }

        if (_serial != null && _invice != null){
            this.ticket.series = _serial;
            this.ticket.invceno = _invice;
            console.info("[TICKET] " + this.ticket.series + " " + this.ticket.invceno);
        }
    }

    private async SendVerifactu(change: TicketChange){
        if (!this.ticket.place){
            return;
        }

        if (this.ticket.place.SendVFTEnabled){
            if (change.verifactu){
                console.warn("[TICKET] overwriting verifactu instance will bave no effect");
            }
            else {
                const verifactu = new Verifactu(null, this.data);
                if (verifactu){
                    verifactu.change = change;
                    if (await verifactu.Refresh()){
                        change.AddVerifactu(verifactu)
                    }
                }
            }
        }
    }

    private async SendTicketbai(change: TicketChange){
        if (!this.ticket.place){
            return;
        }

        if (this.ticket.place.SendBAIEnabled){
            if (change.ticketbai){
                console.warn("[TICKET] overwriting ticketbai instance will bave no effect");
            }
            else {
                const ticketbai = new Ticketbai(null, this.data);
                if (ticketbai){
                    ticketbai.change = change;
                    await ticketbai.Refresh();
                    if (ticketbai.xml){
                        change.AddTicketbai(ticketbai)
                    }
                }
            }
        }
    }
}

/****************************/
/* OFFER RESOLUTION         */
/****************************/

/**
 * One application of an offer over the ticket lines. `units[i]` holds how many
 * units the application takes from the line at index `i` of the resolution.
 */
interface OfferApplication {
    offer: OfferView;
    units: Array <number>;
}

/**
 * A slot is one **group** of an offer (`OfferProduct.grouped` / `MenuView`):
 * applying the offer once takes a single unit for each of its slots. Any
 * product listed in that group can fill the slot (OR within the group).
 * `lines` holds the index of every ticket line whose product can fill it.
 */
interface OfferSlot {
    lines: Array <number>;
}

/**
 * An offer as seen by the resolution: the slots it needs, the price it charges
 * for one application and the best savings it could reach on its own.
 */
interface OfferEntry {
    view: OfferView;
    price: number;
    slots: Array <OfferSlot>;
    alone: number;
    alonecount: number;
}

/**
 * Combinations a search may weigh before it settles for the best one found.
 *
 * Choosing offers is a set packing problem, so a ticket with many offers over
 * the same products has more combinations than can be weighed one by one. The
 * budget keeps the resolution bounded on those tickets; every real one is far
 * below it and is therefore resolved exactly.
 */
const OFFER_BUDGET = 20000;

/**
 * Fills the slots demanded by a set of offer applications with the units of the
 * ticket lines, taking the most expensive lines first.
 *
 * Filling walks alternating paths, so committing an expensive line can move an
 * already placed cheaper unit to another slot instead of being rejected. That
 * makes the resulting covered price the highest the demanded slots allow.
 *
 * The topology of lines and slots is fixed on construction and every buffer is
 * reused, because the resolution fills the very same slots thousands of times
 * with different demands.
 */
class SlotFiller {
    private readonly _flow: Array <Array <number>> = [];
    private readonly _lineslots: Array <Array <number>> = [];
    private readonly _order: Array <number> = [];

    private readonly _lineused: Array <number>;
    private readonly _slotused: Array <number>;
    private readonly _fromline: Array <number>;
    private readonly _fromslot: Array <number>;
    private readonly _seenline: Array <boolean>;
    private readonly _seenslot: Array <boolean>;
    private readonly _queue: Array <number>;

    private _slotdemand: Array <number> = [];

    constructor(
        private readonly _prices: Array <number>,
        private readonly _units: Array <number>,
        private readonly _slotlines: Array <Array <number>>
    ){
        for (let _line = 0; _line < _prices.length; _line++){
            this._flow.push(new Array <number> (_slotlines.length).fill(0));
            this._lineslots.push([]);
            this._order.push(_line);
        }

        for (let _slot = 0; _slot < _slotlines.length; _slot++){
            for (const _line of _slotlines[_slot]){
                this._lineslots[_line].push(_slot);
            }
        }

        this._order.sort((a, b) => _prices[b] - _prices[a]);

        this._lineused = new Array <number> (_prices.length).fill(0);
        this._slotused = new Array <number> (_slotlines.length).fill(0);
        this._fromline = new Array <number> (_slotlines.length).fill(-1);
        this._fromslot = new Array <number> (_prices.length).fill(-1);
        this._seenline = new Array <boolean> (_prices.length).fill(false);
        this._seenslot = new Array <boolean> (_slotlines.length).fill(false);
        this._queue = new Array <number> (_prices.length).fill(0);
    }

    /** Total price of the units that ended up inside an offer. */
    get covered(): number {
        let _covered = 0;
        for (let _line = 0; _line < this._prices.length; _line++){
            _covered += this._lineused[_line] * this._prices[_line];
        }

        return _covered;
    }

    /** Units of each line assigned to each slot, indexed as [line][slot]. */
    get assignment(): Array <Array <number>> {
        return this._flow;
    }

    /** Fill every demanded slot, or report that the ticket can not do it. */
    Fill(slotdemand: Array <number>): boolean {
        this._slotdemand = slotdemand;

        let _demand = 0;
        for (const _amount of slotdemand){
            _demand += _amount;
        }

        for (let _line = 0; _line < this._prices.length; _line++){
            this._lineused[_line] = 0;
            this._flow[_line].fill(0);
        }

        this._slotused.fill(0);
        if (_demand == 0){
            return true;
        }

        let _filled = 0;
        for (const _line of this._order){
            while ((this._lineused[_line] < this._units[_line]) && (_filled < _demand)){
                const _pushed = this.Augment(_line);
                if (_pushed <= 0){
                    break;      // this line can not reach any slot left to fill
                }

                _filled += _pushed;
            }

            if (_filled >= _demand){
                break;
            }
        }

        return (_filled >= _demand);
    }

    /**
     * Route as many units of the line as possible into a slot still to be
     * filled, displacing already placed units to other slots when needed.
     */
    private Augment(line: number): number {
        this._fromline.fill(-1);
        this._fromslot.fill(-1);
        this._seenline.fill(false);
        this._seenslot.fill(false);

        this._queue[0] = line;
        this._seenline[line] = true;

        let _head = 0;
        let _tail = 1;
        let _target = -1;

        while ((_head < _tail) && (_target < 0)){
            const _current = this._queue[_head++];

            for (const _slot of this._lineslots[_current]){
                if (this._seenslot[_slot]){
                    continue;
                }

                this._seenslot[_slot] = true;
                this._fromline[_slot] = _current;

                if (this._slotused[_slot] < this._slotdemand[_slot]){
                    _target = _slot;    // this slot still needs a unit
                    break;
                }

                // the slot is full: the lines holding it may move elsewhere
                for (let _other = 0; _other < this._prices.length; _other++){
                    if (!this._seenline[_other] && (this._flow[_other][_slot] > 0)){
                        this._seenline[_other] = true;
                        this._fromslot[_other] = _slot;
                        this._queue[_tail++] = _other;
                    }
                }
            }
        }

        if (_target < 0){
            return 0;   // no slot left within reach of this line
        }

        // the path can not move more units than its tightest step allows
        let _push = Math.min(
            this._units[line] - this._lineused[line],
            this._slotdemand[_target] - this._slotused[_target]
        );

        let _slot = _target;
        while (this._fromline[_slot] != line){
            const _holder = this._fromline[_slot];

            _push = Math.min(_push, this._flow[_holder][this._fromslot[_holder]]);
            _slot = this._fromslot[_holder];
        }

        _slot = _target;
        while (true){
            const _holder = this._fromline[_slot];
            this._flow[_holder][_slot] += _push;

            if (_holder == line){
                break;
            }

            this._flow[_holder][this._fromslot[_holder]] -= _push;
            _slot = this._fromslot[_holder];
        }

        this._lineused[line] += _push;
        this._slotused[_target] += _push;

        return _push;
    }
}

/**
 * Searches how many times each offer of a group applies so that the ticket ends
 * up as cheap as possible.
 *
 * Offers compete for the same units, and that makes the choice inseparable:
 * ranking them by their own savings and applying them in that order can break a
 * combination worth more. The search therefore weighs how many times every
 * offer is applied, discarding a branch as soon as it can no longer beat the
 * best combination already found.
 */
class OfferSearch {
    private readonly _filler: SlotFiller;
    private readonly _slotoffer: Array <number> = [];
    private readonly _demand: Array <number> = [];
    private readonly _counts: Array <number>;

    private _bestcounts: Array <number>;
    private _best = 0;
    private _budget = OFFER_BUDGET;

    constructor(
        prices: Array <number>,
        units: Array <number>,
        private readonly _offers: Array <OfferEntry>
    ){
        const _slotlines: Array <Array <number>> = [];
        for (let _index = 0; _index < _offers.length; _index++){
            for (const _slot of _offers[_index].slots){
                _slotlines.push(_slot.lines);
                this._slotoffer.push(_index);
                this._demand.push(0);
            }
        }

        this._filler = new SlotFiller(prices, units, _slotlines);
        this._counts = new Array <number> (_offers.length).fill(0);
        this._bestcounts = this._counts.slice(0);
    }

    /** Times each offer must be applied for the cheapest ticket. */
    Resolve(): Array <number> {
        this.Climb();
        this.Search(0, 0);

        return this._bestcounts;
    }

    /**
     * Settle on a decent plan before searching, so the very first branch has a
     * value to beat and a ticket too big to search exhaustively still ends up
     * with a sensible answer.
     *
     * Adding the application that saves most gets trapped whenever an offer
     * eats the units another one needs, so the climb is repeated giving each
     * offer the head start of being applied on its own first.
     */
    private Climb(): void {
        this.Settle(-1);

        for (let _index = 0; _index < this._counts.length; _index++){
            this.Settle(_index);
        }

        this._counts.fill(0);
    }

    /** Climb from the plan that applies only the given offer, as often as it can. */
    private Settle(from: number): void {
        this._counts.fill(0);
        if (from >= 0){
            this._counts[from] = this._offers[from].alonecount;
        }

        let _value = this.Savings() ?? 0;
        while (true){
            let _chosen = -1;
            let _chosenvalue = _value;

            for (let _index = 0; _index < this._counts.length; _index++){
                this._counts[_index] += 1;

                const _candidate = this.Savings();
                if ((_candidate !== null) && (_candidate > _chosenvalue)){
                    _chosenvalue = _candidate;
                    _chosen = _index;
                }

                this._counts[_index] -= 1;
            }

            if (_chosen < 0){
                break;      // no application left that makes the ticket cheaper
            }

            this._counts[_chosen] += 1;
            _value = _chosenvalue;
        }

        if (_value > this._best){
            this._best = _value;
            this._bestcounts = this._counts.slice(0);
        }
    }

    private Search(index: number, savings: number): void {
        if (index >= this._counts.length){
            return;
        }

        let _savings = savings;
        for (let _count = 0; this._budget > 0; _count++){
            this._counts[index] = _count;

            if (_count > 0){
                this._budget--;

                const _value = this.Savings();
                if (_value === null){
                    break;      // the ticket can not hold one more application
                }

                _savings = _value;
                if (_savings > this._best){
                    this._best = _savings;
                    this._bestcounts = this._counts.slice(0);
                }
            }

            // no offer can ever contribute more than what it saves on its own,
            // so this branch is worthless when even that can not beat the best
            let _bound = _savings;
            for (let _next = index + 1; _next < this._counts.length; _next++){
                _bound += this._offers[_next].alone;
            }

            if (_bound > this._best){
                this.Search(index + 1, _savings);
            }
        }

        this._counts[index] = 0;
    }

    private Savings(): number | null {
        let _price = 0;
        for (let _index = 0; _index < this._counts.length; _index++){
            _price += this._counts[_index] * this._offers[_index].price;
        }

        for (let _slot = 0; _slot < this._slotoffer.length; _slot++){
            this._demand[_slot] = this._counts[this._slotoffer[_slot]];
        }

        if (!this._filler.Fill(this._demand)){
            return null;
        }

        return this._filler.covered - _price;
    }
}

/**
 * Resolves which offers of the place apply to a ticket, and how many units of
 * each line every application takes, so that the ticket total is the lowest the
 * catalog allows.
 *
 * The savings of one application are the base price of the units it takes minus
 * the offer price (option charges are added to both sides and cancel out), so a
 * combination is worth the price of every unit it covers minus the price of
 * every offer it uses.
 *
 * The ticket lines are read once on construction, before any offer is bound, so
 * the resolution is not disturbed by the line splitting that binding causes.
 */
class OfferCalc {
    private readonly _lines: Array <TicketProductView> = [];
    private readonly _prices: Array <number> = [];
    private readonly _units: Array <number> = [];
    private readonly _offers: Array <OfferEntry> = [];

    constructor(ticket: TicketView, offers: Array <OfferView>){
        for (const _ticketproduct of ticket.products){
            if (!_ticketproduct.product || !_ticketproduct.InCart){
                continue;
            }

            this._lines.push(_ticketproduct);
            this._prices.push(_ticketproduct.calcinfo.price || 0);
            this._units.push(_ticketproduct.amount || 0);
        }

        this.BuildOffers(offers);
    }

    /** Ticket lines the resolution works on, as they were before any offer. */
    get lines(): Array <TicketProductView> {
        return this._lines;
    }

    /** The applications that leave the ticket as cheap as the catalog allows. */
    Resolve(): Array <OfferApplication> {
        for (const _offer of this._offers){
            this.BestAlone(_offer);
        }

        const _applications: Array <OfferApplication> = [];
        for (const _group of this.SplitGroups()){
            const _members = _group.map(index => this._offers[index]);
            const _counts = new OfferSearch(this._prices, this._units, _members).Resolve();

            for (const _application of this.BuildApplications(_members, _counts)){
                _applications.push(_application);
            }
        }

        return _applications;
    }

    /** Describe every active offer in terms of the lines that can fill its slots. */
    private BuildOffers(offers: Array <OfferView>): void {
        for (const _offer of offers){
            if (!_offer.IsActive){
                continue;
            }

            if (!_offer.groups || _offer.groups.length == 0){
                console.warn("[OFFER] offer '" + (_offer.name?.en || '') + "' does not provide any groups");
                continue;
            }

            // One slot per group: any product listed in the group can fill it
            // (OR within the group). Menu courses are alternatives, not AND.
            const _slots: Array <OfferSlot> = [];
            for (const _group of _offer.groups){
                const _candidates: Array <number> = [];

                for (let _index = 0; _index < this._lines.length; _index++){
                    const _lineproduct = this._lines[_index].product;
                    if (!_lineproduct){
                        continue;
                    }

                    for (const _groupproduct of _group.products){
                        if (_lineproduct.IsChild(_groupproduct.object)){
                            _candidates.push(_index);
                            break;
                        }
                    }
                }

                _slots.push({ lines: _candidates });
            }

            if (_slots.length > 0){
                this._offers.push({ view: _offer, price: _offer.price || 0, slots: _slots, alone: 0, alonecount: 0 });
            }
        }
    }

    /**
     * Most an offer can save when it is the only one applied. Used as an upper
     * bound: sharing the ticket with other offers can only take units away from it.
     */
    private BestAlone(offer: OfferEntry): void {
        const _slotlines = offer.slots.map(slot => slot.lines);
        const _filler = new SlotFiller(this._prices, this._units, _slotlines);

        for (let _count = 1; ; _count++){
            if (!_filler.Fill(_slotlines.map(() => _count))){
                break;      // the ticket can not hold one more application
            }

            const _savings = _filler.covered - (_count * offer.price);
            if (_savings > offer.alone){
                offer.alone = _savings;
                offer.alonecount = _count;
            }
        }
    }

    /**
     * Group the offers that can dispute a line. Offers of different groups never
     * take units from each other, so each group is resolved on its own.
     */
    private SplitGroups(): Array <Array <number>> {
        const _byline = new Map <number, Array <number>> ();
        for (let _index = 0; _index < this._offers.length; _index++){
            for (const _slot of this._offers[_index].slots){
                for (const _line of _slot.lines){
                    const _sharing = _byline.get(_line) || [];
                    if (!_sharing.includes(_index)){
                        _sharing.push(_index);
                    }

                    _byline.set(_line, _sharing);
                }
            }
        }

        const _seen = new Array <boolean> (this._offers.length).fill(false);
        const _groups: Array <Array <number>> = [];

        for (let _index = 0; _index < this._offers.length; _index++){
            if (_seen[_index]){
                continue;
            }

            const _group: Array <number> = [];
            const _queue: Array <number> = [ _index ];
            _seen[_index] = true;

            while (_queue.length > 0){
                const _current = _queue.shift() as number;
                _group.push(_current);

                for (const _slot of this._offers[_current].slots){
                    for (const _line of _slot.lines){
                        for (const _neighbour of _byline.get(_line) || []){
                            if (!_seen[_neighbour]){
                                _seen[_neighbour] = true;
                                _queue.push(_neighbour);
                            }
                        }
                    }
                }
            }

            _groups.push(_group);
        }

        return _groups;
    }

    /** Turn the resolved counts into one entry per application of each offer. */
    private BuildApplications(offers: Array <OfferEntry>, counts: Array <number>): Array <OfferApplication> {
        const _slotlines: Array <Array <number>> = [];
        const _slotdemand: Array <number> = [];
        const _slotoffer: Array <number> = [];

        for (let _index = 0; _index < offers.length; _index++){
            if (counts[_index] == 0){
                continue;
            }

            for (const _slot of offers[_index].slots){
                _slotlines.push(_slot.lines);
                _slotdemand.push(counts[_index]);
                _slotoffer.push(_index);
            }
        }

        if (_slotlines.length == 0){
            return [];
        }

        const _filler = new SlotFiller(this._prices, this._units, _slotlines);
        if (!_filler.Fill(_slotdemand)){
            return [];
        }

        // the units placed in each slot, one entry per application of its offer
        const _placed: Array <Array <number>> = [];
        for (let _slot = 0; _slot < _slotlines.length; _slot++){
            const _entries: Array <number> = [];

            for (let _line = 0; _line < this._lines.length; _line++){
                for (let _unit = 0; _unit < _filler.assignment[_line][_slot]; _unit++){
                    _entries.push(_line);
                }
            }

            _placed.push(_entries);
        }

        const _applications: Array <OfferApplication> = [];
        for (let _index = 0; _index < offers.length; _index++){
            for (let _application = 0; _application < counts[_index]; _application++){
                const _units = new Array <number> (this._lines.length).fill(0);

                for (let _slot = 0; _slot < _slotoffer.length; _slot++){
                    if (_slotoffer[_slot] == _index){
                        _units[_placed[_slot][_application]] += 1;
                    }
                }

                _applications.push({ offer: offers[_index].view, units: _units });
            }
        }

        return _applications;
    }
}

/****************************/
/* CLASS VIEW               */
/****************************/

export class _TicketView extends ViewObject<Ticket> {

    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get ticket(): Ticket{
        return this.object;
    }
             
    constructor(object: Ticket, data: dataService){
        super(object, data);
    }

    override OnDestroy(): void {
        super.OnDestroy();
    }

    /**********************************/
    /* INJECTOR SERVICES              */
    /**********************************/

    private _lang: languageService | null = null;
    private get lang(): languageService {
        if (!this._lang){
            this._lang = this.data.injector.get(languageService);
        }
        return this._lang;
    }
    
    private _toast: toastService | null = null;
    private get toast(): toastService {
        if (!this._toast){
            this._toast = this.data.injector.get(toastService);
        }
        return this._toast;
    }

    private _connect: wsconnectService | null = null;
    private get wslogger(): wsloggerService {
        if (!this._connect){
            this._connect = this.data.injector.get(wsconnectService);
        }
        return this._connect.logger;
    }

    private _state: stateService | null = null;
    private get state(): stateService {
        if (!this._state){
            this._state = this.data.injector.get(stateService);
        }
        return this._state;
    }

    private get IsGuest(): boolean {
        return this.state.Access === 'GUEST';
    }

    /**********************************/
    /* VIEW MANAGED INFORMATION        */
    /**********************************/

    private _validinvoice: TicketInvoice | null | undefined = undefined; 
    private _validproducts: Array <TicketProductView> | null = null;
    private _validoffers: Array <TicketOfferView> | null = null;
    private _validextras: Array <TicketExtraView> | null = null;
    private _validdiscounts: Array <TicketDiscountView> | null = null;
    private _validmixed: Array <MixedPayment> | null = null;
    private _validpinpadpays: Array <PinpadPay> | null = null;
    private _validrefundpays: Array <PinpadPay> | null = null;
    private _validchanges: Array <TicketChange> | null = null;
    private _validlastchange: TicketChange | null = null;
    private _calcpriceinfo: PriceType | null = null;
    private _lineactivity: TicketLinesCount | null = null;
    private _amountOfProduct: Map<ProductView, number> | null = null;

    private RefreshView(targets: string[]){
        if (targets.includes('TICKET')){
            this._calcpriceinfo = null;
            this._validmixed = null;
            this._validpinpadpays = null;
            this._validrefundpays = null;
            this._validproducts = null;
            this._lineactivity = null;
            this._amountOfProduct = null;
            this.DoRefreshView();
        }

        if (targets.includes('QRCODE')){
            this._calcpriceinfo = null;
            this.DoRefreshView();
        }

        if (targets.includes('TICKETPRODUCT')){
            this._calcpriceinfo = null;
            this._validproducts = null;
            this._validmixed = null;
            this._lineactivity = null;
            this._amountOfProduct = null;
            this.DoRefreshView();
        }

        if (targets.includes('TICKETOFFER')){
            this._calcpriceinfo = null;
            this._validoffers = null;
            this._validmixed = null;
            this.DoRefreshView();
        }

        if (targets.includes('TICKETEXTRA')){
            this._calcpriceinfo = null;
            this._validextras = null;
            this._validmixed = null;
            this.DoRefreshView();
        }

        if (targets.includes('TICKETDISCOUNT')){
            this._calcpriceinfo = null;
            this._validdiscounts = null;
            this._validmixed = null;
            this.DoRefreshView();
        }

        if (targets.includes('TICKETMIXEDPAY')){
            this._validmixed = null;
            this.DoRefreshView();
        }

        if (targets.includes('PINPADPAY')){
            this._validpinpadpays = null;
            this._validrefundpays = null;
            this.DoRefreshView();
        }

        if (targets.includes('TICKETCHANGE')){
            this._validchanges = null;
            this._validlastchange = null;
            this.DoRefreshView();
        }

        if (targets.includes('TICKETINVOICE')){
            this._validinvoice = undefined;
            this._validlastchange = null;
            this.DoRefreshView();
        }
    }

    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void{
        const weakref = new WeakRef(this);

        // deregister of current subscriptions
        for (const _subsription of this._subscriptions){
            _subsription.unsubscribe();
        }
        this._subscriptions = [];

        // register to updates on contained items 
        this._subscriptions.push(
            this.ticket.OnRefresh.subscribe(
            (targets: string[]) => {
                const self = weakref.deref();
                if (self){  // do not keep strong references in subscription
                    self.RefreshView(targets);
                }
            })
        );  

        // register for changes in ticket products
        for (const product of this.products){
            this._subscriptions.push(
                product.OnRefresh.subscribe(
                (targets: string[]) => {
                    const self = weakref.deref();
                    if (self){  // do not keep strong references in subscription
                        self.RefreshView(targets);
                    }
                })
            );                  
        }

    }

    /**********************************/
    /* ACTIVE CHILDREN INFORMATION    */
    /**********************************/    

    get till(): Till | null {
        return this.ticket.till;
    }

    set till(value: Till | null) {
        if (!value || this.ticket.till) {
            return;
        }

        this.ticket.till = value;
    }

    get paidin(): Session | null {
        return this.ticket.paidin;
    }

    private get tilldevice(): Till | null {
        if (this.ticket.till) {
            return this.ticket.till;
        }

        const _tillareas = this.place?.TillAreas;
        if (_tillareas?.length === 1) {
            return _tillareas[0].object;
        }

        const _paiddevice = this.ticket.paidin?.device;
        if (_paiddevice){
            const _paidTill = _paiddevice.till;
            if (_paidTill){
                return _paidTill;
            }
        }

        const _tableTill = this.ticket.qrcode?.till ?? null;
        if (_tableTill){
            return _tableTill;
        }

        if (!this.ticket.qrcode){
            const _creator = this.ticket.session?.device;
            if (_creator){
                const _creatorTill = _creator.till;
                if (_creatorTill){
                    return _creatorTill;
                }
            }
        }

        return null;
    }

    get status(): string | null {
        return this.ticket.status;
    }

    set status(value: string) {
        // Auto-ready only when the ticket itself is direct (place-wide
        // TicketPreparation off, and QR tickets without optionQrTrackTicket).
        if (this.ticket.status == 'PR'){
            if (this.ticket.IsDirect){
                this.ticket.status = 'RD';
            }
        }

        this.ticket.status = value;
    }

    /**
     * Ticket already has a till payment request: complete `paidin`/`paidon`/`till`
     * instead of rewriting `payment`/`paidby`. `PAYLINE` is a request too, but
     * the server closes it, so it is not completable here.
     */
    private _IsPayRequest(): boolean {
        return this.IsToPay && this.ticket.payment !== 'PAYLINE';
    }

    /**
     * Single payment apply path: clear, defer (invoice/partial), change medium on
     * already-paid, complete a till request, or settle atomically — branched on
     * ticket state. Print / MarkPaid / till live only here.
     */
    private _ApplyPayment(mode: TicketPayment | null): void {
        if (!mode){
            this.ticket.paidby = null;
            this.ticket.given = null;
            this.ticket.paidon = null;
            this.ticket.paidin = null;
            this.ticket.till = null;
            this.ticket.payment = null;

            this.status = 'AC';
            return;
        }

        if (this.IsPaid){
            if (this.ticket.payment === 'PAYACCOUNT'){
                this.ticket.paidon = new Date();
                this.ticket.paidin = this.data.session;

                this.status = 'PD';
            }

            this.ticket.payment = mode;
            return;
        }

        if (this.ToInvoice){
            this.pendpay = mode;
            return;
        }

        if (this.HasPartial){
            this.OnPartial(mode, this.due);
            return;
        }

        if (this.ticket.invceac == null){
            const _oncash = (mode === 'PAYCASH') && !!this.place?.OpenOnCash;
            const _oncard = (mode === 'PAYCARD') && !!this.place?.OpenOnCard;
            if (_oncash || _oncard){
                // ptf: receipt leaves via the own terminal; the drawer is never opened
                this.Commiter.PrintTicket(this.state.WorkMode !== 'PTF', true, true);
            }
        }

        const _completeRequest = this._IsPayRequest() && (mode === this.ticket.payment);
        if (!_completeRequest){
            this.ticket.paidby = this.data.session;
            this.ticket.payment = mode;
        }

        this.ticket.paidon = new Date();
        this.ticket.paidin = this.data.session;
        this.ticket.MarkPaid();

        if (mode !== 'PAYACCOUNT' && !this.ticket.till){
            this.till = this.tilldevice;
        }
    }

    get invoice(): TicketInvoice | null {
        if (this._validinvoice === undefined){
            this._validinvoice = this.ticket.invoice;
        }

        return this._validinvoice;
    }

    set invoice(value: TicketInvoice | null){
        this.ticket.invoice = value;
        this._validinvoice = undefined;
    }

    get place(): PlaceView | null {
        return this.data.alives.get(this.ticket.place) as PlaceView;
    }

    get qrcode(): QrCodeView | null {
        return this.data.alives.get(this.ticket.qrcode) as QrCodeView;
    }

    set qrcode(value: QrCodeView | null){
        if (this.ticket.qrcode) {
            this.ticket.qrcode.DelTicket(this.ticket);
        }
        this.ticket.qrcode = value?.object ?? null;
        if (this.ticket.qrcode) {
            this.ticket.qrcode.AddTicket(this.ticket);
        }
    }

    get request(): AskWaiter | null {
        if (this.ticket.qrcode){
            for (const _askwaiter of this.ticket.qrcode.requests){
                if ((_askwaiter.ticket === this.ticket) && _askwaiter.IsValid){
                    return _askwaiter;
                }
            }
        }

        return null;
    }

    set request(value: AskWaiter | null){
        if (!this.ticket.qrcode){
            return;
        }

        if (value){
            value.qrcode = this.ticket.qrcode;
        }
    }

    get account(): PayAccountView | null {
        return this.data.alives.get(this.ticket.account) as PayAccountView;
    }

    set account(value: PayAccountView | null) {
        if (this.HasPartial){
            throw new Error('[TICKET] Cannot charge a ticket with partial payment to account');
        }

        this.ticket.account = value?.object ?? null;

        if (value) {
            this.payment = 'PAYACCOUNT';
        }
    }

    get changes(): Array<TicketChange> {
        if (this._validchanges === null){
            const _ticketchanges: Array <TicketChange> = this.ticket.changes.slice(0);
            _ticketchanges.sort((a, b) => {
                const _acreated = a.created.getTime();
                const _bcreated = b.created.getTime();

                return _acreated - _bcreated;
            });
            this._validchanges = _ticketchanges;
        }
        return this._validchanges;
    }

    get lastchange(): TicketChange | null {
        if (this._validlastchange === null){
            const _lastchange = this.changes[this.changes.length - 1];
            if (_lastchange){
                this._validlastchange = (_lastchange.CopyOf || _lastchange) as TicketChange;    
            }
        }

        return this._validlastchange;
    } 

    private _backwardsgroup = false;
    get products(): Array <TicketProductView> {
        if (this._validproducts === null){
            const _products: Array <TicketProductView> = [];

            for(const _product of this.ticket.products){
                if (_product.IsValid){
                    const _productview = this.alives.get(_product) as TicketProductView;
                    if (_productview){
                        _products.push(_productview);
                    }
                }
            }

            // the last sorted product is the first
            _products.sort((a, b) => {
                const _asort: number = a.sort || 0;
                const _bsort: number = b.sort || 0;

                return (_asort - _bsort);
            })

            this._validproducts = _products;

            /*
                Note: Required for backwards compatibilty
                TicketProducts does not provide the quantity in previous versions
                As every entry is one item, must be grouped here in order to work
            */            

            if (!this._backwardsgroup){
                this._backwardsgroup = true;
                this.GroupProducts();
            }
        }

        return this._validproducts;
    }


    /**
     * Order-slip line candidates: splitters, valid lines, and invalid lines with
     * pending notify delta (`GetNotified() !== 0`). Prefer this over
     * `ticket.object.products` from feature code.
     */
    get PrintOrder(): Array<TicketProductView> {
        const _lines: Array<TicketProductView> = [];

        for (const product of this.ticket.products){
            const _view = this.alives.get(product) as TicketProductView | null;
            if (!_view){
                continue;
            }

            if (product.IsSplitter || product.IsValid || (_view.GetNotified() !== 0)){
                _lines.push(_view);
            }
        }

        return _lines.sort((a, b) => (a.sort || 0) - (b.sort || 0));
    }

    /** Total units in the active ticket cart (InCart lines). */
    get ProductsLength(): number {
        let length = 0;

        for (const product of this.products){
            if (product.InCart){
                length += product.Count;
            }
        }

        return length;
    }

    /**
     * Units of a catalog product in the active cart (InCart lines only).
     * Cached in one pass over `products`; invalidated on TICKET / TICKETPRODUCT refresh.
     */
    AmountOfProduct(product: ProductView): number {
        if (this._amountOfProduct === null){
            const amounts = new Map<ProductView, number>();

            for (const line of this.products){
                if (!line.InCart){
                    continue;
                }

                const catalog = line.product;
                if (!catalog){
                    continue;
                }

                amounts.set(catalog, (amounts.get(catalog) || 0) + line.Count);
            }

            this._amountOfProduct = amounts;
        }

        return this._amountOfProduct.get(product) || 0;
    }

    /** Ticket-wide cart activity counters with final unit charge applied. */
    get lineActivity(): TicketLinesCount {
        if (this._lineactivity === null){
            let _addAmount = 0;
            let _addIncome = 0;
            let _delAmount = 0;
            let _delIncome = 0;

            for (const _product of this.ticket.products){
                if (_product.IsSplitter){
                    continue;
                }

                const _line = this.alives.get(_product) as TicketProductView | null;
                if (!_line){
                    continue;
                }

                const _add = _line.addlines ?? 0;
                const _del = _line.dellines ?? 0;
                const _charge = Math.max(0, _line.calcinfo.charge || 0);

                _addAmount += _add;
                _delAmount += _del;
                _addIncome += _add * _charge;
                _delIncome += _del * _charge;
            }

            this._lineactivity = {
                addlines: {
                    amount: _addAmount,
                    income: _addIncome,
                },
                dellines: {
                    amount: _delAmount,
                    income: _delIncome,
                },
            };
        }

        return this._lineactivity;
    }

    get offers(): Array <TicketOfferView> {
        if (this._validoffers === null){
            const _offers: Array <TicketOfferView> = [];

            for(const _offer of this.ticket.offers){
                if (_offer.IsValid){
                    const _offerview = this.alives.get(_offer) as TicketOfferView;
                    if (_offerview){
                        _offers.push(_offerview);                        
                    }
                }
            }

            this._validoffers = _offers;
        }

        return this._validoffers;        
    } 

    get extras(): Array <TicketExtraView> {
        if (this._validextras === null){
            const _extras: Array <TicketExtraView> = [];

            for (const _extra of this.ticket.extras){
                if (_extra.IsValid){
                    const _extraview = this.alives.get(_extra) as TicketExtraView;
                    if (_extraview){
                        _extras.push(_extraview);
                    }
                }
            }

            this._validextras = _extras;
        }

        return this._validextras;
    }

    get discounts(): Array <TicketDiscountView> {
        if (this._validdiscounts === null){
            const _discounts: Array <TicketDiscountView> = [];

            for (const _discount of this.ticket.discounts){
                if (_discount.IsValid){
                    const _discountview = this.alives.get(_discount) as TicketDiscountView;
                    if (_discountview){
                        _discounts.push(_discountview);
                    }
                }
            }

            this._validdiscounts = _discounts;
        }

        return this._validdiscounts;
    }

    /**
     * Cash/card split for the ticket. Persisted `mixed[]` when payment is
     * PAYMIXED; otherwise read-only synthetic PAYCASH/PAYCARD entries (unused method at 0).
     */
    get mixed(): Array <MixedPayment> {
        if (this._validmixed === null){
            const _mixedpayments: Array <MixedPayment> = [];

            for (const _mixed of this.ticket.mixed){
                _mixedpayments.push(_mixed);
            }

            if (_mixedpayments.length === 0 && this.IsPaid){
                const _total = this.ticket.price || this.priceinfo.val || 0;
                if (_total > 0 && (this.payment === 'PAYCASH' || this.payment === 'PAYCARD')){
                    // Read-only synthetics: do not set `.ticket` — MixedPayment.DoChildLinked
                    // would AddMixed and persist them on the ticket.
                    const _cash = new MixedPayment(null, this.data);
                    _cash.payment = 'PAYCASH';
                    _cash.amount = (this.payment === 'PAYCASH') ? _total : 0;

                    const _card = new MixedPayment(null, this.data);
                    _card.payment = 'PAYCARD';
                    _card.amount = (this.payment === 'PAYCARD') ? _total : 0;

                    _mixedpayments.push(_cash, _card);
                }
            }

            this._validmixed = _mixedpayments;
        }

        return this._validmixed;
    }

    /**********************************/
    /* PINPAD CARD PAYMENTS           */
    /**********************************/

    /**
     * Non-discarded card-terminal sale attempts for this ticket, oldest first.
     * Excludes refund rows (`kind === 'RE'`).
     */
    get PinpadPays(): Array <PinpadPay> {
        if (this._validpinpadpays === null){
            const _rows: Array <PinpadPay> = [];
            for (const _row of this.ticket.pinpadpays){
                if (_row.status !== 'DE' && !_row.IsRefund){
                    _rows.push(_row);
                }
            }
            _rows.sort((a, b) => {
                const _as = a.started ? new Date(a.started).getTime() : 0;
                const _bs = b.started ? new Date(b.started).getTime() : 0;
                return _as - _bs;
            });
            this._validpinpadpays = _rows;
        }
        return this._validpinpadpays;
    }

    /**
     * Non-discarded card-terminal refund attempts for this ticket, oldest first.
     */
    get RefundPays(): Array <PinpadPay> {
        if (this._validrefundpays === null){
            const _rows: Array <PinpadPay> = [];
            for (const _row of this.ticket.pinpadpays){
                if (_row.status !== 'DE' && _row.IsRefund){
                    _rows.push(_row);
                }
            }
            _rows.sort((a, b) => {
                const _as = a.started ? new Date(a.started).getTime() : 0;
                const _bs = b.started ? new Date(b.started).getTime() : 0;
                return _as - _bs;
            });
            this._validrefundpays = _rows;
        }
        return this._validrefundpays;
    }

    /**
     * Most recent pending or amount-mismatch refund attempt (`PE`/`MM`).
     */
    get RefundPending(): PinpadPay | null {
        let _found: PinpadPay | null = null;
        for (const _row of this.RefundPays){
            if (_row.status === 'PE' || _row.status === 'MM'){
                _found = _row;
            }
        }
        return _found;
    }

    /**
     * Most recent approved (`AP`) or manually declared (`MA`) refund that is not yet
     * registered on the ticket (`applied === false`).
     */
    get RefundToApply(): PinpadPay | null {
        let _found: PinpadPay | null = null;
        for (const _row of this.RefundPays){
            if ((_row.status === 'AP' || _row.status === 'MA') && _row.applied !== true){
                _found = _row;
            }
        }
        return _found;
    }

    /**
     * Most recent pending or amount-mismatch attempt (blocks a new terminal charge).
     */
    get PinpadPending(): PinpadPay | null {
        let _found: PinpadPay | null = null;
        for (const _row of this.PinpadPays){
            if (_row.status === 'PE' || _row.status === 'MM'){
                _found = _row;
            }
        }
        return _found;
    }

    /** Most recent approved card-terminal attempt. */
    get PinpadApproved(): PinpadPay | null {
        let _found: PinpadPay | null = null;
        for (const _row of this.PinpadPays){
            if (_row.status === 'AP'){
                _found = _row;
            }
        }
        return _found;
    }

    /**
     * Most recent `AP` or `MA` row that still has a merchant or customer voucher
     * (`Receipts`). Pure manual `MA` without receipts yields null (option hidden).
     */
    get PinpadVoucher(): PinpadPay | null {
        let _found: PinpadPay | null = null;
        for (const _row of this.PinpadPays){
            if (_row.status !== 'AP' && _row.status !== 'MA'){
                continue;
            }
            const _receipts = _row.Receipts;
            if (_receipts.merchant || _receipts.customer){
                _found = _row;
            }
        }
        return _found;
    }

    /** Most recent card payment declared manually by the waiter (`MA`). */
    get PinpadManual(): PinpadPay | null {
        let _found: PinpadPay | null = null;
        for (const _row of this.PinpadPays){
            if (_row.status === 'MA'){
                _found = _row;
            }
        }
        return _found;
    }

    /**
     * Approved (`AP`) or manually declared (`MA`) attempt that has not yet closed the ticket
     * (`applied === false`).
     */
    get PinpadToApply(): PinpadPay | null {
        let _found: PinpadPay | null = null;
        for (const _row of this.PinpadPays){
            if ((_row.status === 'AP' || _row.status === 'MA') && _row.applied !== true){
                _found = _row;
            }
        }
        return _found;
    }

    /**
     * True when {@link RefundToApply} exists and its amount equals `cardAmount` in cents:
     * the terminal already approved that refund and only registration remains.
     */
    RefundCovers(cardAmount: number): boolean {
        const _row = this.RefundToApply;
        if (!_row){
            return false;
        }
        return Math.round(Number(_row.amount ?? 0) * 100) === Math.round(cardAmount * 100);
    }

    /**
     * Marks a refund row as registered on the ticket (`applied = true`) after
     * {@link OnReturn} / commit. Does not change payment or mixed.
     */
    async CloseRefund(row: PinpadPay): Promise<void> {
        if (!row || !row.IsRefund){
            return;
        }
        if (row.applied === true){
            return;
        }
        row.applied = true;
        this._validrefundpays = null;
        await row.DoCommit(true);
    }

    /**
     * Sale row a full card refund may link to, or null for a loose refund.
     * Does **not** choose a terminal — the host resolves that via Place inventory
     * (`origin.pinpad` when still `IsValid` / `OwnPinpad` / `pinpads` / picker).
     */
    RefundOrigin(cardAmount: number): PinpadPay | null {
        if (this.payment !== 'PAYCARD'){
            return null;
        }

        const _row = this.PinpadApproved;
        if (!_row || _row.status !== 'AP' || _row.applied !== true){
            return null;
        }
        if (!_row.operation){
            return null;
        }
        if (_row.Orders.length > 1){
            return null;
        }
        if (Math.round(cardAmount * 100) !== Math.round(Number(_row.amount ?? 0) * 100)){
            return null;
        }

        for (const _refund of this.RefundPays){
            if (_refund.origin === _row.operation){
                return null;
            }
        }

        return _row;
    }

    /**
     * True when {@link PinpadToApply} can be settled on this ticket alone.
     * An `AP` row whose amount differs from the current ticket amount becomes `MM`,
     * is committed, and returns false. Joint charges must call this on every ticket
     * before any {@link ApplyPinpad} (orchestrator: `paytefService.SettleSales`).
     */
    async ReadyPinpadApply(): Promise<boolean> {
        const _row = this.PinpadToApply;
        if (!_row){
            return false;
        }

        if (_row.status === 'AP'){
            const _ticketAmount = Math.round(Number(this.due ?? this.object?.price ?? 0) * 100);
            const _paidAmount = Math.round(Number(_row.amount ?? 0) * 100);
            if (_ticketAmount !== _paidAmount){
                _row.status = 'MM';
                await _row.DoCommit(true);
                return false;
            }
        }

        return true;
    }

    /**
     * Closes this ticket with its unapplied `AP` or `MA` card attempt: the single
     * per-ticket closing path of card charges. Runs {@link ReadyPinpadApply} first;
     * on success marks the row `applied` and {@link DoPayment}(`PAYCARD`) **in memory
     * only**. Persistence is the host's following {@link OnCommit} / `CommitTicket`,
     * which flushes the ticket and its `PINPADPAY` child together — so `applied`
     * never reaches the server without the paid ticket. Multi-ticket settle is
     * orchestrated outside this view (`paytefService.SettleSales`).
     */
    async ApplyPinpad(): Promise<boolean> {
        if (!(await this.ReadyPinpadApply())){
            return false;
        }

        const _row = this.PinpadToApply;
        if (!_row){
            return false;
        }

        _row.applied = true;
        // Keep the persisted attempt in sync when the panel holds a CopyOf mirror.
        let _canonical: PinpadPay | null = _row;
        let _up = _row.CopyOf;
        while (_up instanceof PinpadPay) {
            _canonical = _up;
            _up = _canonical.CopyOf;
        }
        if (_canonical && _canonical !== _row) {
            _canonical.applied = true;
        }
        this.ticket.DoRefresh('PINPADPAY', false);
        await this.DoPayment('PAYCARD');

        return true;
    }

    /**********************************/
    /* TICKET BEHAVIOUR               */
    /**********************************/

    config(item: string){
        void item;
        return null;    // TODO: determine the ticket behaviour based on the place configuration
    }

    /**********************************/
    /* CUSTOM METHODS                 */
    /**********************************/

    get empty(): boolean {
        return (this.products.length == 0);
    }

    get maxprice(): number {
        return AppConstants.invoiceMaxPrice;
    }

    /**
     * Ordinary sale with an economic 0% VAT bucket. TicketBAI only accepts that
     * rate on a full invoice whose recipient NIF starts with R, G, Q, N or W.
     */
    get ZeroRateInvoice(): boolean {
        let _base = 0;
        let _amount = 0;

        for (const _split of this.priceinfo.tax?.split ?? []){
            if (Math.round((_split.rate || 0) * 100) / 100 !== 0){
                continue;
            }
            _base += _split.base || 0;
            _amount += _split.taxes || 0;
        }

        for (const _split of this.priceinfo.dsc?.split ?? []){
            if (Math.round((_split.rate || 0) * 100) / 100 !== 0){
                continue;
            }
            _base -= _split.base || 0;
            _amount -= _split.taxes || 0;
        }

        return Math.round(_base * 100) / 100 !== 0 || Math.round(_amount * 100) / 100 !== 0;
    }

    /** Sale at or above the simplified-invoice limit. */
    get MaxPriceInvoice(): boolean {
        return this.priceinfo.val >= this.maxprice;
    }

    get ToInvoice(): boolean {
        return !this.IsCancel && (this.ticket.invoice == null) && (
            this.MaxPriceInvoice || this.ZeroRateInvoice
        );
    }

    /**********************************/
    /* CLASS MEMBERS                  */
    /**********************************/

    get payment(): TicketPayment | null {
        return this.ticket.payment;
    }

    set payment(value: TicketPayment | null){
        if (this.ticket.payment == value){
            return;   // no change — does not complete a pending request
        }

        this._ApplyPayment(value);
    }

    /**
     * Applies a payment mode for QRA and POS.
     * - `GUEST`, or any `PAYLINE`: pending request (`PP`, no `MarkPaid`).
     *   Callers persist the ticket row; `TICKETDATA` already exists from
     *   `CommitTicket`. Stripe/server seals commercial payments on confirm.
     * - Staff access: `_ApplyPayment` (settle, complete request, or change medium).
     *   Same mode on a till request completes it (unlike the `payment` setter).
     */
    async DoPayment(mode: TicketPayment): Promise<void> {
        // Complete an existing till request before the GUEST/PAYLINE request gate
        // (same mode on PP — e.g. OnCommit at the bar).
        if (this._IsPayRequest() && (mode === this.ticket.payment)){
            this._ApplyPayment(mode);
            return;
        }

        const _request = mode === 'PAYLINE' || this.IsGuest;
        if (_request) {
            if (mode !== 'PAYCASH' && mode !== 'PAYCARD' && mode !== 'PAYLINE') {
                throw new Error('[TICKET] DoPayment request mode invalid: ' + mode);
            }

            this.ticket.paidon = null;
            this.ticket.paidin = null;
            this.ticket.paidby = this.data.session;
            this.ticket.till = null;
            this.ticket.payment = mode;
            this.status = 'PP';
            return;
        }

        this._ApplyPayment(mode);
    }

    /**
     * Cancels a pending guest payment request: restores the ticket as if the
     * request was never made (`AC`, no `payment`/`paidby`/`given`, flags clear,
     * product `PP` events removed). Callers flush afterwards.
     */
    CancelPayment(): void {
        for (const product of this.products) {
            if (product.status !== 'DE') {
                product.DelEvent('PP');
            }
        }

        this._ApplyPayment(null);
    }

    private _pendpay: TicketPayment | null = null;
    get pendpay(): TicketPayment | null {
        return this._pendpay;
    }

    set pendpay(payment: TicketPayment | null){
        this._pendpay = payment;
    }

    /**********************************/
    /* TICKET STATUS                  */
    /**********************************/

    get IsEmpty(): boolean {
        return this.ProductsLength === 0;
    }

    /**
     * Never-persisted empty ticket that never held a real product: may be
     * dropped in memory without cancel registry / orderno / toast.
     */
    get CanDiscard(): boolean {
        return this.IsEmpty
            && this.IsNewTicket
            && !this.IsCancel
            && !this.ticket.ToCommitFlag;
    }

    /**
     * Ticket is opened (editable / reusable) when not cancelled and not paid, with:
     * - `PR` (draft): always open from creation — table reuse uses `IsRecent && IsOpened`
     * - table ticket (`qrcode`): stays open while unpaid (after send too)
     * - bar / no table: if ready and place requires prepayment, closed until paid
     */
    get IsOpened(): boolean {
        if (this.IsCancel){
            return false;
        }

        // Draft from creation: visible in qrcode.tickets reuse filter immediately
        if (this.status == 'PR'){
            return true;
        }

        if (this.IsPaid){
            return false;
        }

        // Table order: stay open after PR→AC so reopening the mesa reuses this ticket
        if (this.ticket.qrcode){
            return true;
        }

        if (!this.IsReady){
            return true;
        }

        if (this.place){
            return !this.place.TicketPrepayment;
        }

        return false;
    }

    /**
     * Ticket is closed when
     * it is not opened
     */
    get IsClosed(): boolean {
        return !this.IsOpened;
    }

    /**
     * Order is ready for the guest (direct service, status `RD`, or ready flag).
     */
    get IsReady(): boolean {
        return this.ticket.IsReady;
    }

    /**
     * Ticket belongs to the current session, is valid, and is still recent.
     */
    get InSession(): boolean {
        return this.ticket.InSession;
    }

    /**
     * Flag is marked when paiment requested
     * return the flag value
     */
    get IsToPay(): boolean {
        return this.ticket.ToPayFlag;
    }

    /**
     * Flag is marked when paid
     * return the flag value
     */
    get IsPaid(): boolean {
        return this.ticket.PaidFlag;
    }

    /**
     * Whether this ticket is paid and the paying session is the current session —
     * equivalent to v3 `ticket.paidby == session`, kept inside the view so features
     * never need to reach into `.object` to compare payer identity.
     * Instance identity is enough: SESSION rows resolve through storage/`ObjectFactory`.
     */
    get PaidInSession(): boolean {
        return this.IsPaid && (this.ticket.paidby === this.data.session);
    }

    /**
     * Ticket is cancelled when:
     * Cancellation has been requested
     * and no refunds are pending
     */
    get IsCancel(): boolean {
        if (this.ticket.IsCancelled){
            return (this.ToRefund == 0);
        }

        return false;
    }

    /**********************************/
    /* TICKET PRICE                   */
    /**********************************/

    Reset(): PriceType {
        return this._PriceInfo(true);
    }

    get priceinfo(): PriceType {
        if (this._calcpriceinfo === null){
            this._calcpriceinfo = this._PriceInfo((this.ticket.CopyOf != null) || this.ticket.ToInsert);
        }

        return this._calcpriceinfo;
    }

    /**
     * Amount already collected via in-progress partial payments.
     * Derived from TICKETMIXEDPAY rows with `partial === true`; not a TICKET column.
     */
    get paid(): number {
        let _sum = 0;
        for (const _row of this.mixed){
            if (_row.partial === true){
                _sum += _row.amount || 0;
            }
        }
        return Math.round(_sum * 100) / 100;
    }

    /** Remaining amount to collect: ticket total minus {@link paid}, floored at 0. */
    get due(): number {
        return Math.max(0, Math.round((this.priceinfo.val - this.paid) * 100) / 100);
    }

    /** True while the ticket is still open and at least one partial payment has been registered. */
    get HasPartial(): boolean {
        return !this.IsPaid && this.paid > 0;
    }

    get ToRefund(): number {
        return 0;
    }

    /**********************************/
    /* CART LINE GROUPS (RETURNS)     */
    /**********************************/

    get ProductsToPrepare(): Array<TicketProductView> {
        return this.products.filter(product => product.Display && product.InCart);
    }

    /**
     * Pending order-slip delta filter (`GetNotified() !== 0`).
     * Owned by `TicketOrder`; used to decide emit and, after device print,
     * which lines still need `SetNotified`.
     */
    get ProductsToOrder(): Array<TicketProductView> {
        return this.Commiter.ProductsToOrder;
    }

    /**********************************/
    /* INTERNAL CLASSES               */
    /**********************************/    

    private _ticketcommit: TicketCommit | null = null;
    private get Commiter(): TicketCommit {
        if (this._ticketcommit === null){
            this._ticketcommit = new TicketCommit(this.data, this.alives.get(this.ticket) as TicketView);
        }
        return this._ticketcommit;
    }

    /**********************************/
    /* TICKET ACTIONS                 */
    /**********************************/    

    private _onTicketUpdated = new Subject <void> ();
    OnTicketUpdated = this._onTicketUpdated.asObservable();

    /** Tracks unsent local changes for `CanSend`; reset on every `OnCommit`. */
    private _dirty = false;

    get dirty(): boolean {
        return this._dirty;
    }

    set dirty(value: boolean) {
        this._dirty = value;
    }

    get CanPrint(): boolean {
        return true;    // TODO
    }

    get OnPrint(){
        return this.Commiter.OnPrint;
    }

    get OnOrder(){
        return this.Commiter.OnOrder;
    }

    private _printed = false;   // avoids multiple logging of the same event
    LogPrint(info: unknown, charge: number | null = null): boolean {
        const place = this.place;
        if (!place || !info || this._printed){
            return false;
        }

        this._printed = true;
        setTimeout(() => {
            this._printed = false;
        }, 0);

        this.data.events.LogAction(LogActions.RECEIPT, {
            place,
            info,
            charge
        });

        this.wslogger.LogReceipt(place.object, this.ticket, info);

        return true;
    }

    /** True when there are local changes not yet sent: a new ticket, or a dirty copy of an already-persisted one. */
    get CanSend(): boolean {
        if (!this.place?.AllowTicketCommit || this.empty){
            return false;
        }

        return this.ticket.CopyOf ? this.dirty : this.ticket.ToInsert;
    }

    /**
     * Whether this ticket must be paid before the kitchen/bar starts preparing it —
     * either because the place always requires prepayment, or because it requires
     * it specifically for QR/guest orders. Equivalent to v3 `ticket.IsPrepay`.
     */
    get IsPrepay(): boolean {
        if (this.place?.IsPrepay){
            return true;
        }

        if (this.ticket.source === TicketSource.QR){
            return this.place?.GetConfigOption('optionQrPrePayment', false) ?? false;
        }

        return false;
    }

    /** True when this is a brand-new ticket (not yet sent, not editing a copy of an existing one). */
    get IsNewTicket(): boolean {
        return this.ticket.ToInsert && !this.ticket.CopyOf;
    }

    get CanCash(): boolean {
        if (!this.place){
            return false;
        }

        if (!this.place.AllowTicketPayment || !this.place.CanCash){
            return false;
        }

        return this._Payable();
    }

    get CanCard(): boolean {
        if (!this.place){
            return false;
        }

        if (!this.place.AllowTicketPayment || !this.place.CanCard){
            return false;
        }

        return this._Payable();
    }

    private _Payable(): boolean {
        if (this.empty || this.IsPaid || this.IsCancel){
            return false;
        }

        // Unconfirmed card-terminal attempt: cash/card stay visible but disabled until unlock.
        if (this.PinpadPending){
            return false;
        }

        return this.IsOpened || this.IsToPay;
    }

    /** True when the diner count can be edited: ticket is tied to a table and not cancelled. */
    get CanSetDiners(): boolean {
        return this.qrcode !== null && !this.ticket.IsCancelled;
    }

    get CanAccount(): boolean {
        if (!this.place){
            return false;
        }

        return this.place.CanAccount;
    }   
    
    DoPrint(oncommit = false): void {
        this.Commiter.PrintTicket(false, false, oncommit);
    }

    DoOrder(): void {
        this.Commiter.OrderTicket();
    }

    /**
     * Reprints the full order slip to every kitchen/bar destination as a copy
     * (`OrderAction` `'cpy'`). Does not require pending line delta.
     */
    DoReorder(): void {
        this.Commiter.CopyOrder();
    }

    async OnCommit(payment: TicketPayment | null, transaction: dataTransaction | null = null): Promise <void> {
        this.dirty = false;

        if (payment !== null){
            await this.DoPayment(payment);
        }

        await this.Commiter.CommitTicket(payment, transaction);

        if (this.ticket.IsDeleted){    
            return;     // do not notify deleted tickets
        }

        if (this.ticket.IsCancelled){
            this.toast.ShowAlert('danger', this.lang.tr('@place_ticket_cancelled_toast'), 2000);
        }
        else {
            this.toast.ShowAlert('primary', this.lang.tr('@place_ticket_sent_toast'), 2000);
        }
    }    

    OnOpen(): void {
        // remove ticket payment information
        this.ticket.invoice = null;
        this.ticket.account = null;
        this.ticket.session = this.data.session;
        this.ticket.paidby = null;
        this.ticket.paidin = null;
        this.ticket.paidon = null;
        this.ticket.till = null;
        this.ticket.payment = null;
        this.ticket.ReopenedFlag = true;

        // restore ticket status to 'opened'
        this.status = 'AC';
    }

    OnCancel(): void {
        this.status = 'CC';
    }

    /**
     * Drop an unpersisted never-product ticket without cancel registry.
     * Marks `DE` so uncommitted parent links (`place`/`qrcode`) release it.
     */
    OnDiscard(): void {
        if (!this.CanDiscard){
            return;
        }

        this.status = 'DE';
    }

    OnReady(): void {
        this.status = 'RD';
    }

    /**
     * Registers a cumulative partial payment for one method on an open ticket.
     * The first call resolves `paidby`/`paidin`/`paidon`/`till` without closing.
     * When collected amount covers the ticket total, finalizes payment and status
     * without re-entering the `payment` setter (attribution/till already set).
     * The UI orchestrates commit.
     *
     * @param payment - Payment method for this advance (`PAYMIXED` / `PAYACCOUNT` not allowed).
     * @param amount - Amount to add (must be > 0 and ≤ due).
     */
    OnPartial(payment: TicketPayment, amount: number): void {
        if (this.empty || this.IsCancel || this.IsPaid || this.IsToPay){
            throw new Error('[TICKET] Cannot register partial payment on this ticket');
        }

        if ((payment === 'PAYACCOUNT') || (payment === 'PAYMIXED')){
            throw new Error('[TICKET] Partial payment method is invalid');
        }

        const _amount = Math.round((amount || 0) * 100) / 100;
        const _due = this.due;

        if (_amount <= 0 || _amount > _due){
            throw new Error('[TICKET] Partial payment amount is invalid');
        }

        if (this.paid === 0){
            this.ticket.paidby = this.data.session;
            this.ticket.paidon = new Date();
            this.ticket.paidin = this.data.session;
            this.till = this.tilldevice;
        }

        this._AddPartial(payment, _amount);
        this._validmixed = null;

        if (this.due === 0){
            this.ticket.payment = this._ResolvePayment();
            this.ticket.MarkPaid();

            if (!this.ticket.till){
                this.till = this.tilldevice;
            }
        }
    }

    OnMerge(merge: TicketView, options: MergeOptions | null = null): void {
        const allowDiscountLoss = options?.allowDiscountLoss === true;

        if (allowDiscountLoss){
            for (const ticketdiscount of this.discounts){
                ticketdiscount.status = 'DE';
            }
        }
        else {
            for (const ticketdiscount of merge.discounts){
                const discount: DiscountView | null = ticketdiscount.discount;
                if (!discount || !discount.AppliesTo(this.alives.get(this.ticket) as TicketView)){
                    throw new Error('[TICKET] Discount cannot be applied on merge');
                }
            }
        }

        this.dirty = true;

        for (const ticketproduct of merge.products){
            if (!ticketproduct.product){
                continue;
            }

            const _line = ticketproduct.object;
            const _source = _line.ticket;
            if (_source){
                _source.DelProduct(_line);
            }

            _line.ticket = this.ticket;
            this.ticket.AddProduct(_line);
        }

        if (!allowDiscountLoss){
            for (const ticketdiscount of merge.discounts){
                if (ticketdiscount.discount){
                    this.AddDiscount(ticketdiscount.discount);
                }
            }
        }

        merge.status = 'DE';

        // Consolidate equal lines and recalculate TICKET.price / taxes
        // (same close as OnReturn). Reparent alone leaves duplicate rows.
        this.Reset();

        // Recycle tombstones (DE, no refund) must not stay on the surviving AC
        // ticket: after a split both lines are already committed, so soft-DE
        // rows remain in TICKETPRODUCT and inflate COUNT/SUM server-side.
        // Rehome them onto the absorbed (DE) ticket. Return lines (refund > 0)
        // stay on the survivor. Relation uses merge.ticket (same as this.ticket):
        // schema `>` setters require a DataObject, not the view Proxy.
        for (const _row of [...this.ticket.products]){
            if ((_row.status === 'DE') && ((_row.refund ?? 0) === 0)){
                this.ticket.DelProduct(_row);
                _row.ticket = merge.ticket;
                merge.AddProduct(_row);
            }
        }

        this._onTicketUpdated.next();
    }

    OnSplit(selections: Array <TicketUnits>): TicketView {
        if (selections.length === 0){
            throw new Error('[TICKET] Cannot split ticket without selected products');
        }

        if (!this.ticket.place){
            throw new Error('[TICKET] Cannot split ticket without place relation');
        }

        if (!this.data.session){
            throw new Error('[TICKET] Cannot split ticket without active session');
        }

        const split = new Ticket(null, this.data);
        if (split){
            split.created = new Date();
            split.updated = new Date();
            split.status = 'PR';
            split.session = this.data.session;
            split.place = this.ticket.place;
            split.qrcode = this.ticket.qrcode;
        }

        const splitview = this.alives.get(split) as TicketView;
        if (!splitview){
            throw new Error('[TICKET] Unable to resolve split ticket view');
        }

        for (const selection of selections){
            if (selection.units <= 0){
                continue;
            }

            for (let idx = 0; idx < selection.units; idx++){
                const detached = this.SplitProduct(selection.product);
                if (!detached){
                    throw new Error('[TICKET] Unable to detach all selected units for split');
                }

                this.ticket.DelProduct(detached.object);
                detached.object.ticket = split;
                split.AddProduct(detached.object);
            }
        }

        return splitview;
    }

    /**
     * Marks the selected units as returned and recomputes the ticket in one step.
     *
     * Paid refundable lines move directly to `DE` with `refund` registered.
     * Unpaid lines become `DE` without refund. On a partial return, the units
     * that stay active are the ones detached into a new line, while the
     * returned units remain on the original line (which keeps whatever
     * persisted identity it already had) before being marked `DE`. When
     * `refund` is provided, mixed payment entries are adjusted (and converted
     * to `PAYMIXED` when a cross-method refund is applied on a non-mixed
     * ticket). The UI orchestrates selection and commit.
     */
    OnReturn(selections: Array <TicketUnits>, refund: { cash: number; card: number } | null = null): void {
        if (selections.length === 0){
            throw new Error('[TICKET] Cannot return ticket without selected products');
        }

        // Track the lines actually returned, so their refund can be filled in
        // (for paid tickets) once the split above resolves each line's charge.
        const _returned: Array <TicketProductView> = [];

        for (const selection of selections){
            if (selection.units <= 0){
                continue;
            }

            if (selection.units >= selection.product.Count){
                selection.product.status = 'CC';
                _returned.push(selection.product);
            }
            else {
                const _offer = selection.product.offer;
                const _unitcharge = _offer ? selection.product.calcinfo.charge : null;
                const _keepunits = selection.product.Count - selection.units;

                for (let idx = 0; idx < _keepunits; idx++){
                    const kept = this.SplitProduct(selection.product);
                    if (!kept){
                        throw new Error('[TICKET] Unable to detach all selected units for return');
                    }

                    if (_offer){
                        kept.offer = _offer;
                    }
                }

                if (this.IsPaid && !selection.product.refund && ((_unitcharge || 0) > 0)){
                    selection.product.refund = _unitcharge as number;
                }

                selection.product.status = 'CC';
                _returned.push(selection.product);
            }
        }

        if (this.IsPaid){
            for (const _return of _returned){
                if (!_return.refund){
                    const _charge = _return.charge || _return.calcinfo.charge || 0;
                    if (_charge > 0){
                        _return.refund = _charge;
                    }
                }
            }
        }

        if (refund){
            this._DoRefund(refund);
        }

        this.Reset();

        if (this.IsEmpty){
            this.OnCancel();
        }
    }

    ToCart(product: ProductView, options: Array <ProductOptView>, amount: number, info: ToCartInfo | null){
        if (!(amount > 0)){
            return;
        }

        this.dirty = true;

        const _ticketproduct = new TicketProduct(null, this.data);
        if (_ticketproduct){
            _ticketproduct.ticket = this.ticket;
            _ticketproduct.product = product.object;
            _ticketproduct.amount = 0;
            _ticketproduct.taxrate = product.taxrate; 
            _ticketproduct.status = 'AC';

            if (info){
                _ticketproduct.market = info.market || null;
                _ticketproduct.weight = info.weight || null;
                _ticketproduct.comments = info.comments || null;
                _ticketproduct.type = info.type || null;

                if ((info.fixed !== null) && (info.fixed !== undefined)){
                    _ticketproduct.fixed = true;
                    _ticketproduct.charge = info.fixed;
                }
            }

            for(const _option of options){
                const _ticketoption = new TicketOption(null, this.data);
                if (_ticketoption){
                    _ticketoption.product = _ticketproduct;
                    _ticketoption.option = _option.object;
                    _ticketoption.status = 'AC';

                    _ticketproduct.AddOption(_ticketoption);
                }
            }
        }

        const _ticketproductview = this.alives.get(_ticketproduct) as TicketProductView;
        if (_ticketproductview){
            this.AddTicketProduct(_ticketproductview, this.products.length, false, amount);
        }

        this._onTicketUpdated.next();
    }

    SetAmount(ticketproduct: TicketProductView, value: number): void {
        this.dirty = true;
        ticketproduct.SetLines(Math.max(0, value));
        this._onTicketUpdated.next();
    }

    AddOneOf(ticketproduct: TicketProductView): void {
        this.dirty = true;

        const _copy = ticketproduct.object.Copy() as TicketProduct;
        if (_copy){
            _copy.CopyOf = null;            // this is not a copy any more
            
            _copy.Reset();

            _copy.offer = null;
            _copy.sort = null;
            _copy.charge = _copy.fixed ? (_copy.charge || 0) : null;
            _copy.refund = 0;
        }

        const _ticketproductview = this.alives.get(_copy) as TicketProductView;
        if (_ticketproductview){
            this.AddTicketProduct(_ticketproductview, (ticketproduct.sort || 0) + 1, false, 1);
        }

        this._onTicketUpdated.next();
    }

    SplitProduct(product: TicketProductView): TicketProductView | null {
        this.dirty = true;

        let node: TicketProductView | null = product;
        while (node?.DisplayFr){
            node = node.DisplayFr;
        }

        while (node){
            const units = node.Count;
            if (units <= 0){
                node = node.DisplayTo;
                continue;
            }

            const detached: TicketProductView | null = node.Remove(1);
            if (detached){
                detached.DisplayTo = null;
                const left = node.Count;
                if (left === 0){
                    node.DisplayTo = null;
                }
    
                this.AddTicketProduct(detached, (product.sort ? product.sort + 1 : 0), true);

                this._onTicketUpdated.next();
                return detached;
            }
        }

        this._onTicketUpdated.next();

        return null;
    }

    DelOneOf(ticketproduct: TicketProductView): void {
        this.dirty = true;

        const products: Array <TicketProductView> = [];
        for (const _ticketproduct of this.products){
            if (_ticketproduct.IsJoin(ticketproduct)){
                products.push(_ticketproduct);
            }
        }

        // find the suitable one with greater amount
        let _candidate: TicketProductView = ticketproduct;
        for (const _ticketproduct of products){
            if (_ticketproduct.amount > _candidate.amount){
                _candidate = _ticketproduct;
            }
        }

        const _amount = _candidate.amount || 0;
        const _take = Math.round(Math.min(1, _amount) * 100) / 100;
        if (_take > 0){
            _candidate.DelLines(_take);
        }

        this._onTicketUpdated.next();
    }

    AddSplit(){
        this.dirty = true;

        const _ticketproduct = new TicketProduct(null, this.data);
        if (_ticketproduct){
            _ticketproduct.status = 'SP';
            // Index before linking: ticket setter AddProduct via DoChildLinked.
            const _where = this.products.length;
            _ticketproduct.ticket = this.ticket;

            const _ticketproductview = this.alives.get(_ticketproduct) as TicketProductView;
            if (_ticketproductview){
                this.AddTicketProduct(_ticketproductview, _where);
            }
        }

        this._onTicketUpdated.next();
    }

    AddDiscount(discount: DiscountView){
        this.dirty = true;
        this.AddTicketDiscount(discount);
        this._onTicketUpdated.next();
    }

    /**********************************/
    /* RECYCLE PRODUCTS               */
    /**********************************/

    private _torecycle: Array <TicketProductView> = [];

    private AddRecycle(ticketproduct: TicketProductView): void {
        ticketproduct.status = 'RC';
        this._torecycle.push(ticketproduct);
    }

    private GetRecycle(ticketproduct: TicketProductView): TicketProductView {
        for (let _idx = this._torecycle.length-1; _idx >= 0; _idx --){
            if (ticketproduct.IsJoin(this._torecycle[_idx])){
                const _recycled = this._torecycle.splice(_idx, 1)[0];
                
                if (_recycled){ // adjust to the requested item
                    _recycled.amount = ticketproduct.amount;
                    _recycled.status = ticketproduct.status;
                }
                
                return _recycled;
            }
        }

        return ticketproduct;   // not found (cannot be reused)
    }

    private DelRecycle(){
        for (const ticketproduct of this._torecycle){
            ticketproduct.status = 'DE';
        }
    }

    /**********************************/
    /* PRIVATE METHODS                */
    /**********************************/

    /**
     * Adds amount to the existing partial TICKETMIXEDPAY row for the method, or creates one.
     */
    private _AddPartial(payment: TicketPayment, amount: number): void {
        let _row = this.ticket.mixed.find(
            entry => entry.partial === true && entry.payment === payment
        ) ?? null;

        if (!_row){
            _row = new MixedPayment(null, this.data);
            _row.payment = payment;
            _row.amount = 0;
            _row.partial = true;
            _row.ticket = this.ticket;
            this.ticket.AddMixed(_row);
        }

        _row.amount = Math.round(((_row.amount || 0) + amount) * 100) / 100;
    }

    /**
     * Resolves final payment mode from accumulated partial rows.
     * One method → that method; several → `PAYMIXED`.
     */
    private _ResolvePayment(): TicketPayment {
        const _methods = new Set <TicketPayment> ();

        for (const _row of this.ticket.mixed){
            if (_row.partial === true && (_row.amount || 0) > 0 && _row.payment){
                _methods.add(_row.payment as TicketPayment);
            }
        }

        if (_methods.size === 0){
            throw new Error('[TICKET] Cannot finalize partial payment without amounts');
        }

        if (_methods.size === 1){
            return _methods.values().next().value as TicketPayment;
        }

        return 'PAYMIXED';
    }

    /**
     * Applies refund amounts to the cash/card split. Updates persisted mixed[]
     * or materializes net entries and switches to PAYMIXED on cross-method refunds.
     */
    private _DoRefund(refund: { cash: number; card: number }): void {
        if ((refund.cash || 0) <= 0 && (refund.card || 0) <= 0){
            return;
        }

        const _origCash = this.mixed.find(entry => entry.payment === 'PAYCASH')?.amount || 0;
        const _origCard = this.mixed.find(entry => entry.payment === 'PAYCARD')?.amount || 0;
        const _netCash = Math.max(0, _origCash - (refund.cash || 0));
        let _netCard = Math.max(0, _origCard - (refund.card || 0));

        if (_origCash === 0 && _origCard > 0){
            _netCard = Math.max(0, _origCard - (refund.cash || 0) - (refund.card || 0));
        }

        if (_netCash === _origCash && _netCard === _origCard){
            return;
        }

        if (this.ticket.mixed.length === 0 && this.payment !== 'PAYMIXED'){
            const _crossMethod =
                (_origCash > 0 && _origCard === 0 && (refund.card || 0) > 0) ||
                (_origCard > 0 && _origCash === 0 && (refund.cash || 0) > 0);

            if (!_crossMethod){
                return;
            }
        }

        if (this.ticket.mixed.length > 0){
            const _cashEntry = this.mixed.find(entry => entry.payment === 'PAYCASH');
            const _cardEntry = this.mixed.find(entry => entry.payment === 'PAYCARD');

            if (_cashEntry){
                _cashEntry.amount = _netCash;
            }

            if (_cardEntry){
                _cardEntry.amount = _netCard;
            }
        }
        else {
            const _cashPayment = new MixedPayment(null, this.data);
            _cashPayment.payment = 'PAYCASH';
            _cashPayment.amount = _netCash;
            _cashPayment.ticket = this.ticket;

            const _cardPayment = new MixedPayment(null, this.data);
            _cardPayment.payment = 'PAYCARD';
            _cardPayment.amount = _netCard;
            _cardPayment.ticket = this.ticket;

            this.ticket.AddMixed(_cashPayment);
            this.ticket.AddMixed(_cardPayment);
            this.ticket.payment = 'PAYMIXED';
        }

        this._validmixed = null;
    }

    private _PriceInfo(recalculate = false): PriceType {
        const _calcpriceinfo = new PriceInfo();

        if (recalculate){
            this._torecycle = [];
            this.ClearOffers();
            this.GroupProducts(false);
            this.ApplyOffers();
            this.GroupProducts(true);    
            this.CleanOffers()

            this.DelRecycle();
        }

        // apply products and offers into the final price
        for(const _ticketproduct of this.products){
            if (recalculate){
                _ticketproduct.Recalculate();
            }

            const _info = _ticketproduct.calcinfo;
            const _charge: number = _info.charge || 0;
            const _amount: number = _info.amount || 0;

            const _p: number = _charge * _amount;
            const _r: number = (_ticketproduct.taxrate ?? AppConstants.defaultTaxRate)
            
            _calcpriceinfo.Add(_p, _r); 
        }

        // offers charge the products already applied above
        if (recalculate){
            for (const _ticketoffer of this.offers){
                _ticketoffer.Recalculate();
            }
        }

        // apply extras into the final price
        this.ApplyExtras();
        for(const _ticketextra of this.extras){
            if (recalculate){
                _ticketextra.Recalculate();
            }

            const _info = _ticketextra.calcinfo;
            const _p: number = _info.charge - _info.refund;
            const _r: number = (_ticketextra.taxrate ?? AppConstants.defaultTaxRate);

            _calcpriceinfo.Add(_p, _r);                 
        }

        // apply discounts into the final price
        if (recalculate){
            this.ApplyDiscounts();
        }

        for (const _ticketdiscount of this.discounts){
            const _info = _ticketdiscount.calcinfo;
            _calcpriceinfo.Del(_info.charge - _info.refund);
        }

        const _calculatedprice = _calcpriceinfo.price;

        if (recalculate){   // update the ticket information
            this.ticket.price = _calculatedprice.tax.total;
            this.ticket.taxes = _calculatedprice.tax.taxes;
        }

        return _calculatedprice;
    }

    private _GroupProducts(ticketproducts: Array <TicketProductView>){
        // a returned line is a closed record: it can never absorb or be absorbed
        const _products_toequal: Array <TicketProductView> = ticketproducts.filter(product => product.InCart);
        if (_products_toequal.length < 2){
            return;     // nothing to be grouped 
        }

        while (_products_toequal.length > 0){
            const product = _products_toequal.shift();
            if (product){
                let _isequal: TicketProductView | null = null;

                for(const _product of _products_toequal){
                    // product will be joined into _product
                    if (_product.IsEqual(product)){     
                        _isequal = _product;    
                    }

                    if (_isequal != null){
                        break;      // already found a product to group
                    }
                }

                if (_isequal){
                    _isequal.Merge(product)     // merge the product into the equivalent
                    this.AddRecycle(product);   // add the product to be recycled (or removed)
                }
            }
        }
    }

    private _DisplayProducts(ticketproducts: Array <TicketProductView>){
        const _products_togroup: Array <TicketProductView> = ticketproducts.slice(0);
        if (_products_togroup.length < 2){
            return;     // nothing to be grouped 
        }

        while (_products_togroup.length > 0){
            const product = _products_togroup.shift();
            if (product){
                let _display: TicketProductView | null = null;

                for(const _product of _products_togroup){
                    // product will be displayed into _product
                    if (_product.IsGroup(product)){     
                        _display = _product;    
                    }

                    if (_display != null){
                        break;      // already found a product to display
                    }
                }

                product.DisplayTo = _display;
                if (_display){
                    product.sort = _display.sort;
                }
            }
        }
    }

    private _DisplayOffers(){
        const _offers_togroup: Array <TicketOfferView> = this.offers.slice(0);
        if (_offers_togroup.length < 2){
            return;     // nothing to be grouped 
        }

        while (_offers_togroup.length > 0){
            const offer = _offers_togroup.shift();
            if (offer){
                let _display: TicketOfferView | null = null;

                for(const _offer of _offers_togroup){
                    // offer will be displayed into _offer
                    if (_offer.IsGroup(offer)){     
                        _display = _offer;    
                    }

                    if (_display != null){
                        break;      // already found a product to display
                    }
                }

                offer.DisplayTo = _display;
            }
        }
    }

    private _CreateSections(): Array <TicketProductView[]> {
        const sections: Array <TicketProductView[]> = [];
        const _products = this.products.slice(0);

        let _section: Array<TicketProductView> | null = null;        
        for (const ticketproduct of _products){
            if (_section == null){
                _section = [];
            }

            if (ticketproduct.IsSplitter){
                if (_section.length > 0){
                    sections.push(_section);
                    _section = null;
                }
                else {  // no products in section (remove splitter)
                    ticketproduct.status = 'DE';
                }
            }
            else {
                _section.unshift(ticketproduct);
            }
        }

        if (_section){  // last section
            sections.unshift(_section);
        }

        return sections;
    }

    private GroupProducts(display = false): void {
        // clear previous display grouping
        for(const ticketproduct of this.products){
            ticketproduct.DisplayTo = null;
        }

        if (this.products.length < 2){
            return;     // nothing to be grouped 
        }
        
        // merge products by equality  
        const sections: Array <TicketProductView[]> = this._CreateSections();
        for(const section of sections){
            this._GroupProducts(section);           
        }

        // refresh the product sorting order
        let _sort = 0;
        for (const ticketproduct of this.products){
            ticketproduct.sort = _sort;
            if (ticketproduct.Display){
                _sort = _sort + 1;
            }
        }

        this.doRegister();   // refresh the registration items

        if (display){
            for(const section of sections){
                this._DisplayProducts(section);  // merge products for display    
            }

            this._DisplayOffers();  // merge offers for display
        }
    }

    private AddTicketProduct(ticketproduct: TicketProductView, where: number | null, force = false, qty?: number){
        const _toadd = qty ?? ticketproduct.amount ?? 1;

        if (!ticketproduct.IsSplitter && !(_toadd > 0)){
            return;
        }

        if (!ticketproduct.IsSplitter){
            this.ticket.ToCommitFlag = true;
        }

        // try to increase an existing ticketproduct
        let _candidate: TicketProductView | null = null;
        if (!force){    // if force: we need to add the product (do not reuse)
            const _section: Array <TicketProductView> = [];
            for(const _ticketproduct of this.products){
                if (_ticketproduct.IsSplitter){
                    const _pivot = _ticketproduct.sort;
                    if ((where !== null) && (_pivot >= where)){
                        break;      // must be in this section
                    }

                    _section.splice(0);
                }
                else {
                    _section.push(_ticketproduct);
                }
            }

            for(const _ticketproduct of _section){
                if (ticketproduct.IsEqual(_ticketproduct)){
                    _candidate = _ticketproduct;
                }
    
                if (_candidate != null){
                    break;  // found a candidate to add to
                }
            }    
        }

        if (_candidate){
            _candidate.AddLines(_toadd);
        }
        else {  // no candidate to increase: include the new ticketproduct
            if (!force){
                ticketproduct.AddLines(_toadd);
            }

            if (where !== null){
                ticketproduct.sort = where;
    
                // make the gap for the new product
                for (const product of this.products){
                    const _oldsort = product.sort || 0;
                    if (_oldsort >= ticketproduct.sort){
                        product.sort = _oldsort + 1;
                    }
                }
            }
    
            this.ticket.AddProduct(ticketproduct.object);
        }
    }

    /**
     * Bind one application of the offer to the given units of each ticket line.
     * A line that keeps free units is split, so the caller gets back the line
     * that holds the remainder for the applications still to come.
     */
    private AddTicketOffer(offer: OfferView, appliedproducts: Map <TicketProductView, number>): Map <TicketProductView, TicketProductView> {
        const remainders = new Map <TicketProductView, TicketProductView> ();

        // create the ticket offer instance
        const ticketoffer = new TicketOffer(null, this.data);
        if (ticketoffer){
            ticketoffer.offer = offer.object;
            ticketoffer.ticket = this.ticket;
            ticketoffer.amount = 1;
            ticketoffer.status = 'AC';

            this.ticket.AddOffer(ticketoffer);
        }

        // add the ticket offer to the products
        for (const [ ticketproduct, amount ] of appliedproducts){
            let appliedto: TicketProductView | null = null; 
            if (ticketproduct.amount == amount){
                appliedto = ticketproduct;                      // apply the offer to this product directly
            }
            else {
                const remainder = ticketproduct.Remove(ticketproduct.amount - amount);
                if (remainder){
                    const _recycled = this.GetRecycle(remainder);     // attempt to recycle existing ticketproducts
                    this.AddTicketProduct(_recycled, (ticketproduct.sort ? ticketproduct.sort + 1 : 0), true);
                    appliedto = ticketproduct;
                    remainders.set(ticketproduct, _recycled);
                }
                else {
                    throw new Error("[TICKET] Error applying offer (" + (offer.name?.en || '') + ") to ticket!")
                }    
            }

            if (appliedto){
                appliedto.offer = this.alives.get(ticketoffer) as TicketOfferView;  
            }
        }

        return remainders;
    }

    private AddTicketExtra(extra: ExtraView){
        const _ticketextra = new TicketExtra(null, this.data);
        if (_ticketextra){
            _ticketextra.ticket = this.ticket;
            _ticketextra.extra = extra.object;
            _ticketextra.taxrate = this.place?.taxrate ?? AppConstants.defaultTaxRate;
            _ticketextra.status = 'AC';

            this.ticket.AddExtra(_ticketextra)
        }
    }

    private AddTicketDiscount(discount: DiscountView){
        const _ticketdiscount = new TicketDiscount(null, this.data);
        if (_ticketdiscount){
            _ticketdiscount.ticket = this.ticket;
            _ticketdiscount.discount = discount.object;
            _ticketdiscount.status = 'AC';

            this.ticket.AddDiscount(_ticketdiscount)
        } 
    }

    private ClearOffers(): void {
        if (this.IsPaid && !this.IsToPay) {
            return;   // this ticket is closed
        }

        for(const ticketproduct of this.products){
            if (ticketproduct.offer){
                ticketproduct.offer = null;
            }
        }
    }

    private ApplyOffers(): void {
        if (this.IsPaid && !this.IsToPay) {
            return;   // this ticket is closed
        }

        const ticketview = this.alives.get(this.ticket) as TicketView;
        if (!ticketview || !this.place){
            return;
        }

        const calc = new OfferCalc(ticketview, this.place.offers);

        // applying an offer splits the lines it takes units from, so every
        // application works on the line that still holds the free units
        const holders = calc.lines.slice(0);

        for (const application of calc.Resolve()){
            const _applied = new Map <TicketProductView, number> ();
            for (let _index = 0; _index < application.units.length; _index++){
                if (application.units[_index] > 0){
                    _applied.set(holders[_index], application.units[_index]);
                }
            }

            for (const [ _consumed, _remainder ] of this.AddTicketOffer(application.offer, _applied)){
                const _index = holders.indexOf(_consumed);
                if (_index >= 0){
                    holders[_index] = _remainder;
                }
            }
        }
    }

    // clear orphan offers (no ticket products left)
    private CleanOffers(): void {
        for (const ticketoffer of this.offers){
            if (ticketoffer.products.length === 0) {
                ticketoffer.status = 'DE';
            }
        }
    }

    private ApplyExtras(): void {
        const ticketview = this.alives.get(this.ticket) as TicketView;
        const appliedextras = new Set <ExtraView>();
        
        // keep or remove any extras currenty aplying
        for (const ticketextra of this.extras){
            if (ticketextra.extra){
                appliedextras.add(ticketextra.extra);
                if (ticketextra.extra.AppliesTo(ticketview)){
                    continue;   // is applying and valid
                }
            }
            ticketextra.status = 'DE';  // does not apply any more
        }

        // check for any other extras that may apply
        if (this.place){
            for (const extra of this.place.extras){
                if (!appliedextras.has(extra) && extra.AppliesTo(ticketview)){
                    this.AddTicketExtra(extra);
                }
            }
        }
    }

    private ApplyDiscounts(): void {
        for (const _ticketdiscount of this.discounts){
            _ticketdiscount.Recalculate();
        }
    }
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type TicketView = ViewType <_TicketView, Ticket>;

export function TicketViewProxy(object: Ticket, data: dataService): TicketView {
    return new _TicketView(object, data).Proxy as TicketView;
}
